import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, DollarSign, CreditCard, AlertCircle, Settings, Send, Mail, MessageSquare, Lock, MoreHorizontal, Link2, Ban } from 'lucide-react';
import { InvoiceSettingsService, InvoiceSettings, defaultInvoiceSettings } from '../services/invoiceSettingsService';
import { useBusiness } from '../contexts/BusinessContext';
import { useModalZIndex, ModalStackProvider } from '../contexts/ModalStackContext';
import RichTextDescription from './RichTextDescription';
import { voidPayment } from '../lib/payments';
import InvoiceEmailModal from './InvoiceEmailModal';
import InvoiceSMSModal from './InvoiceSMSModal';
import InvoiceSettingsModal from './InvoiceSettingsModal';
import RecordPaymentModal from './RecordPaymentModal';
import { sendEmailWithFallback } from '../lib/email';
import { sendSMS } from '../lib/messaging';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

/** Edge functions: non-2xx responses throw before JSON is parsed; body is on error.context */
async function readSupabaseFunctionErrorMessage(error: unknown): Promise<string | undefined> {
  if (!(error instanceof FunctionsHttpError) || !error.context) return undefined;
  try {
    const text = await error.context.text();
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed?.error && typeof parsed.error === 'string') return parsed.error;
    } catch {
      /* not JSON */
    }
    return text || undefined;
  } catch {
    return undefined;
  }
}
import { ProductPhotoService } from '../services/productPhotoService';
import { createInvoiceSentMilestone } from '../lib/bookingMilestones';
import { AdditionalTaxLine, getBookingAdditionalTaxBreakdown } from '../lib/tax-rates';
import { paymentGross, paymentPrincipal, isPaymentCountedForBalance } from '../lib/paymentBreakdown';
import { useBookingInvoice } from '../hooks/useBookingInvoice';
import { serviceLineNet, addonLineNet, totalLineDiscountCents, serviceCompareAtDisplay } from '../lib/invoices/lineDiscounts';
import { updateInvoiceTerms, DEFAULT_TERM_OPTIONS, type InvoiceTermCode } from '../lib/invoices/invoiceTerms';
import { formatBookingInvoiceDate, formatBookingInvoiceDueDate, formatCalendarDate } from '../lib/invoiceDates';
import {
  buildPayInvoiceUrl,
  ensureInvoicePortalPayment,
} from '../lib/invoicePortal';
import {
  getInvoiceWithLines,
  getInvoicePayments,
  voidInvoicePayment,
  createInvoicePaymentLink,
  voidInvoice as voidStandaloneInvoice,
  updateInvoiceBillTo,
  updateInvoicePoNumber,
  type InvoiceRow as StandaloneInvoiceRow,
  type InvoiceLineRow as StandaloneInvoiceLine,
} from '../lib/invoices/standaloneInvoices';
import { isInvoiceLocked, type BookingInvoice } from '../lib/invoices/getInvoiceForBooking';
import AmendInvoiceModal from './invoices/AmendInvoiceModal';

// Custom portal-based toast component for modal contexts
const PortalToast = ({ 
  children, 
  type, 
  onClose 
}: { 
  children: React.ReactNode; 
  type: 'loading' | 'success' | 'error';
  onClose: () => void;
}) => {
  const getIcon = () => {
    switch (type) {
      case 'loading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>;
      case 'success':
        return <div className="rounded-full h-4 w-4 bg-green-500 flex items-center justify-center text-white text-xs">✓</div>;
      case 'error':
        return <div className="rounded-full h-4 w-4 bg-red-500 flex items-center justify-center text-white text-xs">✕</div>;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'loading':
        return 'border-blue-200';
      case 'success':
        return 'border-green-200';
      case 'error':
        return 'border-red-200';
    }
  };

  return createPortal(
    <div 
      className={`fixed left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border ${getBorderColor()} p-4 flex items-center space-x-3 min-w-[300px] max-w-md`}
      style={{ zIndex: 999999, top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
    >
      {getIcon()}
      <div className="flex-1">
        {children}
      </div>
      {type !== 'loading' && (
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-2"
        >
          ×
        </button>
      )}
    </div>,
    document.body
  );
};

// Custom toast function with portal rendering for modal contexts
const createPortalToast = (
  setShowPortalToast: (show: boolean) => void,
  setPortalToastMessage: (message: React.ReactNode) => void,
  setPortalToastType: (type: 'loading' | 'success' | 'error') => void
) => ({
  loading: (message: React.ReactNode, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('loading');
    setShowPortalToast(true);
    return 'portal-toast-loading';
  },
  success: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('success');
    setShowPortalToast(true);
    // Auto-hide success toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-success';
  },
  error: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('error');
    setShowPortalToast(true);
    // Auto-hide error toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-error';
  },
  dismiss: (toastId?: string) => {
    setShowPortalToast(false);
  }
});

interface BetaPaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Standalone (booking-less) invoice mode: pass the invoices.id and an empty
   * bookingId. The modal loads the invoice + its line items + payments itself
   * and every action (PDF, send, links, void) keys on the invoice instead of
   * a booking.
   */
  invoiceId?: string | null;
  /**
   * Standalone mode only: open straight into the inline record-payment form
   * (used by the order modal's "Take Payment" button).
   */
  autoOpenPayForm?: boolean;
  bookingId: string;
  bookingNumber: number;
  bookingData: {
    total_amount: number;
    customer?: {
      id: string;
      first_name: string;
      last_name: string;
      email?: string;
      phone?: string;
      full_name?: string;
    };
    payments?: Array<{
      id: string;
      amount: number;
      tip_amount?: number;
      base_amount?: number;
      surcharge_amount?: number;
      method?: string;
      payment_type?: string;
      status: string;
      pdf_url?: string;
      created_at: string;
      reference?: string;
      stripe_payment_intent_id?: string;
      stripe_checkout_session_id?: string;
      notes?: string;
    }>;
  };
  booking?: any; // Add full booking object for services access
  onUpdate?: () => void | Promise<void>;
  isInvisible?: boolean;
}

export default function BetaPaymentsModal({
  isOpen,
  onClose,
  invoiceId,
  autoOpenPayForm = false,
  bookingId,
  bookingNumber,
  bookingData: bookingDataProp,
  booking: bookingProp,
  onUpdate,
  isInvisible = false
}: BetaPaymentsModalProps) {
  const { selectedBusiness } = useBusiness();
  const isStandalone = !!invoiceId;

  // Apply Payment → opens the standard payment modal (RecordPaymentModal)
  const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);

  // Amend a soft-locked booking invoice (open / partially_paid; fully paid = hard lock)
  const [showAmendInvoice, setShowAmendInvoice] = useState(false);

  // ── Standalone invoice mode: the modal owns its data (invoice + lines + payments). ──
  const [saInvoice, setSaInvoice] = useState<StandaloneInvoiceRow | null>(null);
  const [saLines, setSaLines] = useState<StandaloneInvoiceLine[]>([]);
  const [saPayments, setSaPayments] = useState<any[]>([]);

  const reloadStandalone = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const [withLines, pays] = await Promise.all([
        getInvoiceWithLines(invoiceId),
        getInvoicePayments(invoiceId),
      ]);
      if (withLines) {
        setSaInvoice(withLines.invoice);
        setSaLines(withLines.lines);
      }
      setSaPayments(pays);
    } catch (e) {
      console.error('Failed to load standalone invoice:', e);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (isOpen && isStandalone) void reloadStandalone();
  }, [isOpen, isStandalone, reloadStandalone]);

  // What a standalone invoice actually bills — order, rental(s), subscription(s),
  // or counter sale — so the header can show the connection instead of "not connected".
  const [saSource, setSaSource] = useState<{
    orderNumber: string | null;
    rentalNumbers: string[];
    subscriptionNumbers: string[];
    saleNumber: string | null;
  } | null>(null);

  useEffect(() => {
    if (!isOpen || !isStandalone || !invoiceId) { setSaSource(null); return; }
    let active = true;
    (async () => {
      try {
        const [orderRes, rentalRes, subRes, saleRes] = await Promise.all([
          supabase.from('customer_orders').select('order_number').eq('invoice_id', invoiceId).limit(1).maybeSingle(),
          supabase.from('invoice_line_items').select('rental:rental_agreements(rental_number)').eq('invoice_id', invoiceId).not('rental_agreement_id', 'is', null),
          supabase.from('invoice_line_items').select('subscription:subscription_services(subscription_number)').eq('invoice_id', invoiceId).not('subscription_service_id', 'is', null),
          supabase.from('product_sales').select('sale_number').eq('invoice_id', invoiceId).limit(1).maybeSingle(),
        ]);
        let orderNumber: string | null = (orderRes.data?.order_number as string | undefined) ?? null;
        // Online rental prepay: the invoice hangs off the order via the
        // prebilled stamp on its rental lines, not customer_orders.invoice_id.
        if (!orderNumber) {
          const { data: prebilled } = await supabase
            .from('customer_order_items')
            .select('order:customer_orders(order_number)')
            .eq('prebilled_invoice_id', invoiceId)
            .limit(1)
            .maybeSingle();
          orderNumber = ((prebilled as { order?: { order_number?: string } } | null)?.order?.order_number) ?? null;
        }
        const rentalNumbers = Array.from(new Set(
          ((rentalRes.data || []) as Array<{ rental: { rental_number: string | null } | null }>)
            .map(r => r.rental?.rental_number)
            .filter((n): n is string => !!n)
        ));
        const subscriptionNumbers = Array.from(new Set(
          ((subRes.data || []) as Array<{ subscription: { subscription_number: string | null } | null }>)
            .map(r => r.subscription?.subscription_number)
            .filter((n): n is string => !!n)
        ));
        if (!active) return;
        setSaSource({
          orderNumber,
          rentalNumbers,
          subscriptionNumbers,
          // A counter-sale companion exists for order invoices too — only call
          // it a counter sale when there's no order and no rental behind it.
          saleNumber: !orderNumber && rentalNumbers.length === 0 && subscriptionNumbers.length === 0 && saleRes.data?.sale_number != null
            ? String(saleRes.data.sale_number)
            : null,
        });
      } catch {
        if (active) setSaSource({ orderNumber: null, rentalNumbers: [], subscriptionNumbers: [], saleNumber: null });
      }
    })();
    return () => { active = false; };
  }, [isOpen, isStandalone, invoiceId]);

  // Standalone-only actions: copyable pay link, void invoice. (Recording a
  // payment goes through the standard RecordPaymentModal, same as bookings.)
  const [saPayBusy, setSaPayBusy] = useState(false);
  const [saPayLinkUrl, setSaPayLinkUrl] = useState<string | null>(null);
  const [saPayLinkCopied, setSaPayLinkCopied] = useState(false);

  // "Take Payment" entry point: open the payment modal once the invoice has
  // loaded (needs the balance for the prefilled amount). Once per open.
  const autoPayOpenedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      autoPayOpenedRef.current = false;
      return;
    }
    if (
      autoOpenPayForm &&
      isStandalone &&
      saInvoice &&
      !autoPayOpenedRef.current &&
      saInvoice.status !== 'void' &&
      (saInvoice.amount_due_cents || 0) > 0
    ) {
      autoPayOpenedRef.current = true;
      setIsRecordPaymentOpen(true);
    }
  }, [isOpen, autoOpenPayForm, isStandalone, saInvoice]);

  const saCreatePayLink = async () => {
    if (!invoiceId || !saInvoice) return;
    setSaPayBusy(true);
    try {
      const url = await createInvoicePaymentLink(invoiceId, (saInvoice.amount_due_cents || 0) / 100);
      setSaPayLinkUrl(url);
      await reloadStandalone();
    } catch (e: any) {
      portalToast.error(e?.message || 'Failed to create payment link.');
    } finally {
      setSaPayBusy(false);
    }
  };

  const saCopyPayLink = async () => {
    if (!saPayLinkUrl) return;
    try {
      await navigator.clipboard.writeText(saPayLinkUrl);
      setSaPayLinkCopied(true);
      setTimeout(() => setSaPayLinkCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const saVoidInvoice = async () => {
    if (!invoiceId) return;
    if (!confirm('Void this invoice? This cannot be undone.')) return;
    setSaPayBusy(true);
    try {
      await voidStandaloneInvoice(invoiceId);
      await reloadStandalone();
      portalToast.success('Invoice voided');
      if (onUpdate) { try { const r = onUpdate(); if (r instanceof Promise) await r; } catch { /* non-critical */ } }
    } catch (e: any) {
      portalToast.error(e?.message || 'Failed to void invoice.');
    } finally {
      setSaPayBusy(false);
    }
  };

  /** Standalone invoice adapted to the BookingInvoice shape the modal renders from. */
  const standaloneInvoiceView = useMemo<BookingInvoice | null>(() => {
    if (!saInvoice) return null;
    // Derive per-rate taxable bases from the lines (same as getInvoiceForBooking).
    const baseByRate = new Map<string, number>();
    for (const line of saLines) {
      if (!line.taxable || !Array.isArray(line.tax_components)) continue;
      for (const comp of line.tax_components) {
        const key = `${comp.name}::${comp.rate_bps}`;
        baseByRate.set(key, (baseByRate.get(key) ?? 0) + (line.net_cents || 0));
      }
    }
    const tax_breakdown = (saInvoice.tax_breakdown || []).map((c) => {
      const base = baseByRate.get(`${c.name}::${c.rate_bps}`);
      return base != null ? { ...c, base_cents: base } : c;
    });
    return { ...(saInvoice as any), tax_breakdown } as BookingInvoice;
  }, [saInvoice, saLines]);

  /** Customer synthesized from the invoice's frozen bill-to snapshot. */
  const standaloneCustomer = useMemo(() => {
    if (!saInvoice) return undefined;
    const name = (saInvoice.bill_to_name || '').trim();
    const [first, ...rest] = name.split(/\s+/);
    return {
      id: saInvoice.customer_id || '',
      first_name: first || name || 'Customer',
      last_name: rest.join(' '),
      email: saInvoice.bill_to_email || undefined,
      phone: saInvoice.bill_to_phone || undefined,
      full_name: name || undefined,
    };
  }, [saInvoice]);

  // In standalone mode the props are placeholders — substitute the loaded data
  // so the rest of the modal reads payments/customer/totals transparently.
  const bookingData: BetaPaymentsModalProps['bookingData'] = isStandalone
    ? {
        total_amount: (saInvoice?.total_cents ?? 0) / 100,
        customer: standaloneCustomer,
        payments: saPayments,
      }
    : bookingDataProp;
  const booking: any = isStandalone
    ? (saInvoice ? { customer: standaloneCustomer, payments: saPayments } : undefined)
    : bookingProp;
  const stackZ = useModalZIndex(100000);
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [voidMessage, setVoidMessage] = useState('');
  const [selectedPaymentToVoid, setSelectedPaymentToVoid] = useState<any>(null);
  const [isVoidingPayment, setIsVoidingPayment] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [bookingProducts, setBookingProducts] = useState<any[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [showSendDropdown, setShowSendDropdown] = useState(false);
  // "⋯" overflow menu for secondary invoice actions (payment link / void)
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isInvoiceEmailModalOpen, setIsInvoiceEmailModalOpen] = useState(false);
  const [isInvoiceSMSModalOpen, setIsInvoiceSMSModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
  const [invoiceHistory, setInvoiceHistory] = useState<any[]>([]);
  const [showPortalToast, setShowPortalToast] = useState(false);
  const [portalToastMessage, setPortalToastMessage] = useState<React.ReactNode>(null);
  const [portalToastType, setPortalToastType] = useState<'loading' | 'success' | 'error'>('loading');
  const [isInvoiceSettingsModalOpen, setIsInvoiceSettingsModalOpen] = useState(false);
  const [additionalTaxLines, setAdditionalTaxLines] = useState<AdditionalTaxLine[]>([]);
  const [businessTaxRates, setBusinessTaxRates] = useState<{ name: string; rate: number }[]>([]);

  // New invoice system (live). When present, it is the source of truth for
  // subtotal / discount / tax / total; legacy bookings fall back to booking totals.
  const { invoice: bookingInvoice, refetch: refetchBookingInvoice } = useBookingInvoice(
    isOpen && !isStandalone ? bookingId : undefined,
  );
  const invoice = isStandalone ? standaloneInvoiceView : bookingInvoice;
  const refetchInvoice = isStandalone ? reloadStandalone : refetchBookingInvoice;

  // Customer-level payments allocated to this invoice (Receive Payment on the
  // A/R tab, AI-recorded payments). They have no booking_id, so the booking's
  // payments list can't see them — without this the history shows 0 entries
  // for an invoice the rollup says is paid.
  const [allocatedPayments, setAllocatedPayments] = useState<any[]>([]);
  useEffect(() => {
    let active = true;
    const invoiceId = !isStandalone ? bookingInvoice?.id : null;
    if (!invoiceId || !supabase) {
      setAllocatedPayments([]);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('payment_allocations')
        .select('id, amount_cents, payment:payments(id, amount, method, status, created_at, payment_type, notes, booking_id)')
        .eq('invoice_id', invoiceId);
      if (!active) return;
      const rows = (data || [])
        .filter((a: any) => a.payment && !a.payment.booking_id)
        .map((a: any) => ({
          id: `alloc-${a.id}`,
          // Show the portion applied to THIS invoice, not the whole customer
          // payment (one payment can span several invoices).
          amount: (a.amount_cents || 0) / 100,
          method: a.payment.method,
          status: a.payment.status,
          created_at: a.payment.created_at,
          payment_type: a.payment.payment_type || 'partial',
          tip_amount: 0,
          surcharge_amount: 0,
          notes: a.payment.notes || 'Customer payment applied to this invoice',
          // Not voidable from here — the row is one allocation slice of a
          // customer-level payment, not a booking payment.
          is_allocation: true,
        }));
      setAllocatedPayments(rows);
    })();
    return () => { active = false; };
  }, [isStandalone, bookingInvoice?.id, isOpen]);

  /** Invoice number shown in filenames / subjects / notes ("#12" or booking #). */
  const displayNumber = isStandalone ? (saInvoice?.invoice_number || '') : String(bookingNumber);

  // ── Effective bill-to: the invoice's frozen snapshot (customer by default,
  // custom-editable per invoice) with customer fallback for legacy bookings
  // that have no invoices row. Send flows and the preview both read this.
  const billToCustomer = booking?.customer || bookingData.customer;
  const billTo = useMemo(() => {
    const customerName = billToCustomer
      ? (billToCustomer.full_name || `${billToCustomer.first_name || ''} ${billToCustomer.last_name || ''}`.trim())
      : '';
    return {
      name: invoice?.bill_to_name || customerName,
      email: invoice?.bill_to_email || billToCustomer?.email || '',
      phone: invoice?.bill_to_phone || billToCustomer?.phone || '',
      address: invoice?.bill_to_address || '',
    };
  }, [invoice?.bill_to_name, invoice?.bill_to_email, invoice?.bill_to_phone, invoice?.bill_to_address, billToCustomer]);

  // Inline bill-to editor (only offered when an invoices row exists to store it).
  const [billToFormOpen, setBillToFormOpen] = useState(false);
  const [billToDraft, setBillToDraft] = useState({ name: '', email: '', phone: '', address: '' });
  const [billToSaving, setBillToSaving] = useState(false);

  // Customer PO sits above Bill to. Blank until someone types one.
  const [poDraft, setPoDraft] = useState('');
  const [poSaving, setPoSaving] = useState(false);
  const poSavedRef = useRef('');
  const poSaveInFlight = useRef(false);

  useEffect(() => {
    const saved = (invoice?.po_number || '').trim();
    setPoDraft(saved);
    poSavedRef.current = saved;
  }, [invoice?.id, invoice?.po_number]);

  const savePoNumber = async () => {
    if (!invoice?.id || invoice.status === 'void' || poSaveInFlight.current) return;
    const next = poDraft.trim();
    if (next === poSavedRef.current) return;
    poSaveInFlight.current = true;
    setPoSaving(true);
    try {
      await updateInvoicePoNumber({ invoiceId: invoice.id, poNumber: next || null });
      poSavedRef.current = next;
      await refetchInvoice();
    } catch (e: any) {
      setPoDraft(poSavedRef.current);
      portalToast.error(e?.message || 'Failed to save PO number.');
    } finally {
      poSaveInFlight.current = false;
      setPoSaving(false);
    }
  };

  const openBillToForm = () => {
    setBillToDraft({ name: billTo.name, email: billTo.email, phone: billTo.phone, address: billTo.address });
    setBillToFormOpen(true);
  };

  const saveBillTo = async () => {
    if (!invoice?.id) return;
    setBillToSaving(true);
    try {
      await updateInvoiceBillTo({
        invoiceId: invoice.id,
        name: billToDraft.name,
        email: billToDraft.email,
        phone: billToDraft.phone,
        address: billToDraft.address,
      });
      await refetchInvoice();
      setBillToFormOpen(false);
      portalToast.success('Bill-to updated');
    } catch (e: any) {
      portalToast.error(e?.message || 'Failed to update bill-to.');
    } finally {
      setBillToSaving(false);
    }
  };

  const resetBillToCustomer = async () => {
    if (!invoice?.id) return;
    setBillToSaving(true);
    try {
      const customerName = billToCustomer
        ? (billToCustomer.full_name || `${billToCustomer.first_name || ''} ${billToCustomer.last_name || ''}`.trim())
        : '';
      await updateInvoiceBillTo({
        invoiceId: invoice.id,
        name: customerName,
        email: billToCustomer?.email || null,
        phone: billToCustomer?.phone || null,
        address: (billToCustomer as any)?.address || null,
      });
      await refetchInvoice();
      setBillToFormOpen(false);
      portalToast.success('Bill-to reset to customer');
    } catch (e: any) {
      portalToast.error(e?.message || 'Failed to reset bill-to.');
    } finally {
      setBillToSaving(false);
    }
  };

  // Dev-only: backfill a legacy (payment-free) booking into the new invoice system.
  const [syncingInvoice, setSyncingInvoice] = useState(false);
  const handleSyncToInvoicing = async () => {
    if (!bookingId || syncingInvoice) return;
    setSyncingInvoice(true);
    try {
      const { data, error } = await supabase.rpc('backfill_booking_invoice', { p_booking_id: bookingId });
      if (error) throw error;
      if (!data) {
        portalToast.error('Skipped — booking has payments or no billable lines');
      } else {
        portalToast.success('Synced to new invoicing');
        await refetchInvoice();
      }
    } catch (e) {
      console.error('Failed to sync booking to new invoicing:', e);
      portalToast.error('Failed to sync to new invoicing');
    } finally {
      setSyncingInvoice(false);
    }
  };

  // Per-invoice payment term override (recomputes due_at; realtime refreshes the hook).
  const handleTermChange = async (term: InvoiceTermCode) => {
    if (!invoice) return;
    try {
      if (term === 'custom') {
        await updateInvoiceTerms({ invoiceId: invoice.id, term: 'custom', customDueAt: invoice.due_at || new Date().toISOString() });
      } else {
        const anchor = booking?.end_date || invoice.finalized_at || new Date().toISOString();
        await updateInvoiceTerms({ invoiceId: invoice.id, term, anchor });
      }
      // Booking mode refreshes via the realtime hook; standalone reloads directly.
      if (isStandalone) await reloadStandalone();
    } catch (e) {
      console.error('Failed to update invoice terms:', e);
    }
  };
  const handleCustomDueChange = async (dateStr: string) => {
    if (!invoice || !dateStr) return;
    try {
      await updateInvoiceTerms({ invoiceId: invoice.id, term: 'custom', customDueAt: new Date(`${dateStr}T00:00:00`).toISOString() });
      if (isStandalone) await reloadStandalone();
    } catch (e) {
      console.error('Failed to set custom due date:', e);
    }
  };

  // Create portal toast instance
  const portalToast = createPortalToast(setShowPortalToast, setPortalToastMessage, setPortalToastType);

  // Load invoice history (keyed on the booking, or the invoice when standalone)
  const loadInvoiceHistory = useCallback(async () => {
    if (!bookingId && !invoiceId) return;
    try {
      let query = supabase.from('invoice_history').select('*');
      query = isStandalone ? query.eq('invoice_id', invoiceId) : query.eq('booking_id', bookingId);
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading invoice history:', error);
        return;
      }

      setInvoiceHistory(data || []);
    } catch (error) {
      console.error('Error loading invoice history:', error);
    }
  }, [bookingId, invoiceId, isStandalone]);

  useEffect(() => {
    if (isOpen) {
      void loadInvoiceHistory();
    }
  }, [isOpen, loadInvoiceHistory]);

  useEffect(() => {
    if (isOpen && bookingId) {
      getBookingAdditionalTaxBreakdown(bookingId)
        .then(lines => setAdditionalTaxLines(lines))
        .catch(() => setAdditionalTaxLines([]));
    }
  }, [bookingId, isOpen, booking?.tax_amount]);

  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      supabase
        .from('tax_rates')
        .select('name, rate')
        .eq('business_id', selectedBusiness.id)
        .then(({ data }) => { if (data) setBusinessTaxRates(data); });
    }
  }, [selectedBusiness?.id, isOpen]);

  // Load booking products
  const loadBookingProducts = async () => {
    if (!bookingId) return;

    try {
      const { data, error } = await supabase
        .from('product_booking_sales')
        .select('*')
        .eq('booking_id', bookingId);

      if (error) {
        console.error('Error loading booking products:', error);
        return;
      }

      setBookingProducts(data || []);

      // Load product photos if we have products
      if (data && data.length > 0) {
        const stockItemIds = data.map(p => p.stock_item_id);
        try {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          if (photoMap && typeof photoMap === 'object') {
            Object.entries(photoMap).forEach(([itemId, photo]) => {
              if (photo && typeof photo === 'object' && 'photo_url' in photo) {
                photoUrls[itemId] = (photo as any).photo_url;
              }
            });
          }
          setProductPhotos(photoUrls);
        } catch (photoError) {
          console.error('Error loading product photos:', photoError);
          setProductPhotos({});
        }
      }
    } catch (error) {
      console.error('Error loading booking products:', error);
    }
  };
  
  // Close send dropdown on outside click
  useEffect(() => {
    if (!showSendDropdown) return;
    let mounted = true;
    const close = () => { if (mounted) setShowSendDropdown(false); };
    const raf = requestAnimationFrame(() => {
      document.addEventListener('mousedown', close);
    });
    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', close);
    };
  }, [showSendDropdown]);

  // Close "⋯" menu on outside click (same pattern as the send dropdown)
  useEffect(() => {
    if (!showMoreMenu) return;
    let mounted = true;
    const close = () => { if (mounted) setShowMoreMenu(false); };
    const raf = requestAnimationFrame(() => {
      document.addEventListener('mousedown', close);
    });
    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', close);
    };
  }, [showMoreMenu]);




  // Calculate payment status with tip breakdown
  const totalAmount = bookingData.total_amount || 0;

  // Invoice-aware totals: prefer the live invoice, fall back to legacy booking totals.
  const fmtRateBps = (bps: number) => {
    const pct = bps / 100;
    return Number.isInteger(pct) ? String(pct) : String(parseFloat(pct.toFixed(4)));
  };
  const fmtMoney = (n: number) =>
    '$' + (Math.round(n * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Σ per-line (service + add-on) discounts so the lines can show their own net
  // while the "Discount" total shows only the booking-wide portion. Matches the
  // SQL triggers to the cent, so lines + totals always reconcile to invoice.total.
  const invoiceLineItemsForDisc = (booking?.booking_services || booking?.services || []) as any[];
  const lineDiscountTotalCents = invoice ? totalLineDiscountCents(invoiceLineItemsForDisc) : 0;
  const invoiceView = invoice
    ? {
        subtotal: (invoice.subtotal_cents - lineDiscountTotalCents) / 100,
        discount: (invoice.discount_cents - lineDiscountTotalCents) / 100,
        taxTotal: invoice.tax_cents / 100,
        total: invoice.total_cents / 100,
        taxLines: (invoice.tax_breakdown || [])
          .filter((c) => c.amount_cents !== 0)
          .map((c) => ({
            label:
              c.base_cents != null
                ? `${c.name} (${fmtRateBps(c.rate_bps)}% on ${fmtMoney(c.base_cents / 100)})`
                : `${c.name} (${fmtRateBps(c.rate_bps)}%)`,
            amount: c.amount_cents / 100,
          })),
      }
    : null;
  const effectiveTotal = invoice
    ? invoice.total_cents / 100
    : (booking?.total_amount || bookingData.total_amount || 0);

  const completedPayments =
    bookingData.payments?.filter((p) => isPaymentCountedForBalance(p.status)) || [];
  
  // Calculate base payments (excluding tips)
  const totalPaid = completedPayments.reduce((sum, payment) => sum + paymentPrincipal(payment), 0);
  
  // Calculate deposit payments
  const depositPayments = completedPayments.filter((p: any) => p.payment_type === 'deposit');
  const totalDeposit = depositPayments.reduce((sum, payment) => sum + paymentPrincipal(payment), 0);
  
  // Calculate final payments
  const finalPayments = completedPayments.filter((p: any) => p.payment_type === 'final' || !p.payment_type);
  const totalFinal = finalPayments.reduce((sum, payment) => sum + paymentPrincipal(payment), 0);
  
  // Calculate total tips received (legacy fallback)
  const totalTipsLegacy = completedPayments.reduce((sum, payment) => sum + (payment.tip_amount || 0), 0);
  
  // Calculate total surcharges collected (legacy fallback)
  const totalSurchargesLegacy = completedPayments.reduce((sum, payment) => sum + (payment.surcharge_amount || 0), 0);
  
  // Paid / balance / tips / surcharge come from the invoice's server-side rollup
  // when present, falling back to the JS heuristic for invoice-less bookings.
  const effectivePaid = invoice ? invoice.amount_paid_cents / 100 : totalPaid;
  const totalTips = invoice ? invoice.tips_cents / 100 : totalTipsLegacy;
  const totalSurcharges = invoice ? invoice.surcharge_cents / 100 : totalSurchargesLegacy;
  const balanceOwing = invoice
    ? Math.max(0, invoice.amount_due_cents / 100)
    : Math.max(0, effectiveTotal - totalPaid);

  // Total received (principal + surcharge + tips) for display.
  const totalReceived = invoice
    ? effectivePaid + totalSurcharges + totalTips
    : completedPayments.reduce((sum, payment) => sum + paymentGross(payment), 0) + totalTipsLegacy;

  const paymentStatus = balanceOwing === 0 ? 'paid' : effectivePaid > 0 ? 'partial' : 'unpaid';







  // Fetch invoice settings when modal opens
  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      if (!isOpen || !selectedBusiness?.id) return;
      
      setIsLoadingSettings(true);
      try {
        const settings = await InvoiceSettingsService.getInvoiceSettings(selectedBusiness.id);
        if (settings) {
          setInvoiceSettings(settings);
        } else {
          // No settings found, create default settings with business info
          const defaultSettings: InvoiceSettings = {
            business_id: selectedBusiness.id,
            company_name: selectedBusiness.name || 'Your Business Name',
            company_address: selectedBusiness.address || '123 Business Street\nCity, State 12345',
            company_phone: selectedBusiness.phone || '(555) 123-4567',
            company_email: selectedBusiness.email || 'info@yourbusiness.com',
            company_website: `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
            ...defaultInvoiceSettings
          } as InvoiceSettings;
          setInvoiceSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error fetching invoice settings:', error);
        // Fall back to defaults
        const fallbackSettings: InvoiceSettings = {
          business_id: selectedBusiness.id,
          company_name: selectedBusiness.name || 'Your Business Name',
          company_address: selectedBusiness.address || '123 Business Street\nCity, State 12345',
          company_phone: selectedBusiness.phone || '(555) 123-4567',
          company_email: selectedBusiness.email || 'info@yourbusiness.com',
          company_website: `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
          ...defaultInvoiceSettings
        } as InvoiceSettings;
        setInvoiceSettings(fallbackSettings);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchInvoiceSettings();
  }, [isOpen, selectedBusiness?.id]);

  // Re-render when booking data changes to keep invoice in sync
  useEffect(() => {
    // This effect just forces a re-render when booking changes
    // All the invoice data is already using live booking values
  }, [booking]);

  const formatPaymentReference = (ref: string | null | undefined): string => {
    if (!ref) return 'Payment';
    if (ref === 'unpaid-invoice') return 'Online Payment';
    if (ref === 'queue-intake') return 'Queue Payment';
    if (ref.startsWith('pi_')) return 'Stripe Payment';
    if (ref.startsWith('cs_')) return 'Stripe Checkout';
    return ref;
  };

  // Handle voiding the most recent payment
  const handleVoidPayment = () => {
    if (!booking?.payments || booking.payments.length === 0) return;
    
    const payments = booking.payments;
    const validPayments = payments.filter((payment: any) => 
      payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
    );
    
    if (validPayments.length === 0) return;
    
    // Get the most recent valid payment
    const mostRecentPayment = validPayments[validPayments.length - 1];
    
    // Set the payment to void and open the modal
    setSelectedPaymentToVoid(mostRecentPayment);
    setVoidMessage('');
    setIsVoidModalOpen(true);
  };

  // Confirm the void payment with message
  const confirmVoidPayment = async () => {
    if (!selectedPaymentToVoid) return;
    
    try {
      setIsVoidingPayment(true);
      
      console.log('Voiding payment:', {
        paymentId: selectedPaymentToVoid.id,
        amount: selectedPaymentToVoid.amount,
        voidMessage: voidMessage
      });
      
      // Actually void the payment. Standalone invoice payments go through the
      // RPC so the invoice paid/due rollup recomputes (a plain status update
      // would leave the allocation counted).
      if (isStandalone) {
        await voidInvoicePayment(selectedPaymentToVoid.id);
        if (voidMessage.trim()) {
          await supabase
            .from('payments')
            .update({ notes: `Voided: ${voidMessage.trim()}` })
            .eq('id', selectedPaymentToVoid.id);
        }
        await reloadStandalone();
      } else {
        await voidPayment(selectedPaymentToVoid.id, voidMessage);
      }
      
      console.log('Payment voided successfully');
      
      // Close the modal
      setIsVoidModalOpen(false);
      setSelectedPaymentToVoid(null);
      setVoidMessage('');
      
      // After successful void, call onUpdate to refresh the booking data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error voiding payment:', error);
      alert('Failed to void payment. Please try again.');
    } finally {
      setIsVoidingPayment(false);
    }
  };

  // Generate invoice PDF via server-side edge function (no DOM dependency)
  const generateInvoicePDF = async (): Promise<{ blob: Blob; filename: string; base64: string } | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: isStandalone ? { invoiceId } : { bookingId }
      });

      if (error || !data) {
        let serverMsg: string | undefined;
        if (data && typeof data === 'object' && data !== null && 'error' in data) {
          const e = (data as { error?: unknown }).error;
          if (typeof e === 'string' && e.trim()) serverMsg = e;
        }
        if (!serverMsg) serverMsg = await readSupabaseFunctionErrorMessage(error);
        console.error('Error generating PDF:', error, serverMsg ? { serverError: serverMsg } : {});
        alert(
          serverMsg
            ? `Failed to generate PDF: ${serverMsg}`
            : 'Failed to generate PDF. Please try again.'
        );
        return null;
      }

      const binaryStr = atob(data.base64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });

      return { blob, filename: data.filename || `invoice-${displayNumber}.pdf`, base64: data.base64 };
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
      return null;
    }
  };

  // Download invoice as PDF
  const downloadInvoicePDF = async () => {
    if (isDownloadingPDF) return;
    
    try {
      setIsDownloadingPDF(true);
      
      const pdfData = await generateInvoicePDF();
      if (!pdfData) return;

      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const file = new File([pdfData.blob], pdfData.filename, { type: 'application/pdf' });
        if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: pdfData.filename }).catch(() => {});
        }
      } else {
        const url = URL.createObjectURL(pdfData.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = pdfData.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Revoking synchronously races the browser's async download start and
        // silently cancels it (see invoicePortal.ts which delays for the same
        // reason) — keep the blob URL alive until the download has begun.
        window.setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  // Called from InvoiceEmailModal with user-edited subject/message
  const handleSendInvoiceEmail = async (
    emailSubject: string,
    emailMessage: string,
    allowStripePay: boolean,
    depositAmount?: number,
    saveCardOnFile?: boolean,
    recipientEmail?: string,
  ) => {
    if (!selectedBusiness) {
      portalToast.error('No business selected');
      return;
    }

    // Recipient priority: one-off override → invoice bill-to → customer's saved email.
    const targetEmail = (recipientEmail?.trim() || billTo.email?.trim() || bookingData.customer?.email?.trim() || '');
    if (!targetEmail) {
      portalToast.error('No email address to send to. Enter a recipient email or add one to the customer.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(targetEmail)) {
      portalToast.error(`Invalid email address: "${targetEmail}". Please enter a valid email.`);
      return;
    }

    setIsInvoiceEmailModalOpen(false);

    try {
      setIsSendingEmail(true);

      const loadingToast = portalToast.loading(
        <div>
          <div>Sending invoice...</div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>don't close booking</div>
        </div>
      );

      processInvoiceInBackground(allowStripePay, loadingToast, depositAmount, emailSubject, emailMessage, saveCardOnFile, targetEmail);

    } catch (error) {
      console.error('Error initiating invoice send:', error);
      const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred';
      portalToast.error(`Failed to send invoice: ${errorMsg}`);
      setIsSendingEmail(false);
    }
  };

  // Background processing function for heavy operations
  const processInvoiceInBackground = async (
    allowStripePay: boolean,
    loadingToast?: string,
    depositAmount?: number,
    customSubject?: string,
    customMessage?: string,
    saveCardOnFile?: boolean,
    recipientEmailOverride?: string,
  ) => {
    try {
      const crmCustomerId =
        bookingData.customer?.id ?? (booking as { customer?: { id?: string } } | undefined)?.customer?.id;

      // Generate PDF via server-side edge function (already stored in Supabase Storage)
      const pdfData = await generateInvoicePDF();
      if (!pdfData) return;
      
      const base64Content = pdfData.base64;
      
      console.log('PDF attachment prepared:', {
        filename: pdfData.filename,
        contentLength: base64Content.length,
        contentPreview: base64Content.substring(0, 50) + '...'
      });

      // Generate payment link if balance is owed and Stripe Connect is enabled
      let paymentLinkHtml = '';
      
      const currentTotal = effectiveTotal;
      const payments = booking?.payments || bookingData.payments || [];
      const validPayments = payments.filter((payment: any) => 
        payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
      );
      const totalPaid = invoice
        ? effectivePaid
        : validPayments.reduce((sum: number, payment: any) => sum + paymentPrincipal(payment), 0);
      
      // Use deposit amount if provided, otherwise use full remaining balance
      const isDeposit = depositAmount !== undefined && depositAmount !== null;
      const amountOwing = invoice ? balanceOwing : Math.max(0, currentTotal - totalPaid);
      const paymentAmount = isDeposit ? depositAmount : amountOwing;

      console.log('🔍 Payment link check:', {
        amountOwing,
        hasStripeAccount: !!selectedBusiness?.stripe_connect_account_id,
        onlinePaymentsEnabled: selectedBusiness?.online_payments_enabled,
        stripeConnectAccountId: selectedBusiness?.stripe_connect_account_id
      });

      let unpaidPayment = null; // Store payment ID for invoice history linking

      if (amountOwing > 0 && allowStripePay && selectedBusiness?.stripe_connect_account_id && selectedBusiness?.online_payments_enabled !== false) {
        try {
          console.log('🔗 Generating payment link for invoice...');
          
          // Smart payment record management - prevent duplicates and reuse links
          // For deposits, look for deposit payments; for full payments, look for unpaid invoices
          unpaidPayment = payments.find((p: any) => {
            if (isDeposit) {
              // For deposits, look for pending deposit payments with matching amount
              return p.status === 'pending' && 
                     p.payment_type === 'deposit' &&
                     Math.abs((p.amount || 0) - paymentAmount) < 0.01; // Allow small floating point differences
            } else {
              // For full payments, look for unpaid invoice placeholders
              return p.status === 'pending' && 
                     (p.amount === 0 || p.reference === 'unpaid-invoice');
            }
          });
          
          let paymentUrl = null;
          
          if (!unpaidPayment) {
            console.log('🔍 No existing pending payment found, creating new one...');
            // Create unpaid invoice record. Standalone: keyed on invoice_id (the
            // allocation trigger + public pay page resolve money from the invoice).
            const { data: newPayment, error: paymentError } = await supabase
              .from('payments')
              .insert({
                business_id: selectedBusiness?.id || '',
                ...(isStandalone ? { invoice_id: invoiceId, booking_id: null } : { booking_id: bookingId }),
                amount: isDeposit ? paymentAmount : 0, // Use deposit amount if deposit, otherwise 0 for placeholder
                method: 'credit_card',
                status: 'pending',
                reference: isDeposit ? 'deposit-invoice' : 'unpaid-invoice',
                notes: isDeposit 
                  ? `Deposit invoice #${displayNumber} - Deposit: $${paymentAmount.toFixed(2)}`
                  : `Unpaid invoice #${displayNumber}`,
                payment_token: crypto.randomUUID(),
                payment_type: isDeposit ? 'deposit' : 'final',
                save_card_on_file: !!(saveCardOnFile && crmCustomerId && allowStripePay),
                save_card_customer_id:
                  saveCardOnFile && crmCustomerId && allowStripePay ? crmCustomerId : null,
              })
              .select()
              .single();

            if (paymentError) {
              console.error('Failed to create unpaid payment record:', paymentError);
            } else {
              unpaidPayment = newPayment;
              console.log('✅ Created new unpaid payment record:', unpaidPayment.id);
            }
          } else {
            console.log('✅ Found existing pending payment record:', unpaidPayment.id);
          }

          if (unpaidPayment && allowStripePay) {
            const saveFlag = !!(saveCardOnFile && crmCustomerId);
            await supabase
              .from('payments')
              .update({
                save_card_on_file: saveFlag,
                save_card_customer_id: saveFlag ? crmCustomerId : null,
              })
              .eq('id', unpaidPayment.id);
          }

          if (unpaidPayment) {
            // Check if this payment already has a payment link
            const { data: existingLink, error: linkCheckError } = await supabase
              .from('payment_links')
              .select('payment_url')
              .eq('payment_id', unpaidPayment.id)
              .eq('status', 'active')
              .single();

            if (!linkCheckError && existingLink?.payment_url) {
              // Reuse existing payment link
              paymentUrl = existingLink.payment_url;
              console.log('♻️ Reusing existing payment link:', paymentUrl);
            } else {
              // Generate new payment link
              console.log('🔗 Generating new payment link...');
              const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
                body: { paymentId: unpaidPayment.id }
              });

              if (!linkError && linkData?.paymentUrl) {
                paymentUrl = linkData.paymentUrl;
                console.log('✅ New payment link generated:', paymentUrl);
              } else {
                console.error('❌ Failed to generate payment link:', linkError);
              }
            }

            if (paymentUrl) {
              const paymentButtonText = isDeposit 
                ? `Pay Deposit $${paymentAmount.toFixed(2)}` 
                : `Pay $${amountOwing.toFixed(2)}`;
              const paymentDescription = isDeposit
                ? 'Pay your deposit securely online'
                : 'Pay your invoice securely online';
              
              paymentLinkHtml = `
                <div style="margin: 32px 0; padding: 24px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; text-align: center;">
                  <p style="color: #6b7280; margin: 0 0 20px 0; font-size: 15px; line-height: 1.5;">${paymentDescription}</p>
                  <a href="${paymentUrl}" 
                     style="display: inline-block; background: #007AFF; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 10px; font-weight: 500; font-size: 16px; letter-spacing: -0.01em;">
                    ${paymentButtonText}
                  </a>
                  <p style="color: #9ca3af; margin: 16px 0 0 0; font-size: 12px; line-height: 1.4;">Secure payment processed by Stripe</p>
                </div>
              `;
            }
          }
        } catch (paymentLinkError) {
          console.error('Error generating payment link:', paymentLinkError);
          // Continue without payment link
        }
      }
      
      // Build HTML email body from the user-edited plain text message
      const messageHtml = (customMessage || '').split('\n').map(line =>
        `<p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 8px 0;">${line || '&nbsp;'}</p>`
      ).join('\n');

      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="padding: 40px 32px 32px 32px;">
            <div style="margin-bottom: 32px;">
              <h1 style="color: #000000; margin: 0 0 8px 0; font-size: 28px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.2;">Invoice #${displayNumber}</h1>
              <p style="color: #6b7280; margin: 0; font-size: 15px; line-height: 1.5;">${selectedBusiness.name}</p>
            </div>
            
            ${messageHtml}
            
            ${paymentLinkHtml}
            
          </div>
          <div style="padding: 24px 32px; background: #f9fafb; border-top: 1px solid #e5e7eb;">
            <p style="color: #9ca3af; font-size: 13px; margin: 0; text-align: center; line-height: 1.5;">This is an automated invoice from ${selectedBusiness?.name || 'Business'}</p>
          </div>
        </div>
      `;

      const emailSubject = customSubject || (isDeposit
        ? `Invoice #${displayNumber} from ${selectedBusiness?.name || 'Business'} - Deposit Payment`
        : `Invoice #${displayNumber} from ${selectedBusiness?.name || 'Business'}${amountOwing > 0 ? ' - Pay Online' : ' - Thank You'}`);
      
      const result = await sendEmailWithFallback({
        to: recipientEmailOverride?.trim() || bookingData.customer?.email || '',
        subject: emailSubject,
        body: emailBody,
        businessId: selectedBusiness?.id || '',
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          type: 'application/pdf'
        }]
      });
      
      if (result.success) {
        console.log('✅ Invoice sent successfully in background!');
        if (loadingToast) {
          portalToast.dismiss(loadingToast);
        }
        portalToast.success('Invoice sent successfully! ✅');
        
        // Record invoice history for all invoice sends
        if (selectedBusiness?.id) {
          try {
            const customerName = bookingData.customer?.full_name || 
              `${bookingData.customer?.first_name || ''} ${bookingData.customer?.last_name || ''}`.trim();
            
            // Extract payment URL if payment link was created
            let paymentUrl = null;
            if (paymentLinkHtml) {
              const urlMatch = paymentLinkHtml.match(/href="([^"]+)"/);
              if (urlMatch) {
                paymentUrl = urlMatch[1];
              }
            }
            
            const { error: invoiceHistoryError } = await supabase
              .from('invoice_history')
              .insert({
                business_id: selectedBusiness.id,
                booking_id: isStandalone ? null : bookingId,
                booking_number: isStandalone ? null : bookingNumber,
                invoice_id: isStandalone ? invoiceId : null,
                sent_via: 'email',
                has_payment_link: !!paymentLinkHtml,
                payment_link_url: paymentUrl,
                payment_id: unpaidPayment?.id || null, // Link to payment record if payment link was created
                customer_email: recipientEmailOverride?.trim() || bookingData.customer?.email || null,
                customer_name: customerName || null
              });
            
            if (invoiceHistoryError) {
              console.error('❌ Failed to create invoice history record:', invoiceHistoryError);
            } else {
              console.log('✅ Invoice history record created');
              
              // Create milestone for invoice sent
              if (bookingData.customer?.id && selectedBusiness?.id && bookingId) {
                try {
                  await createInvoiceSentMilestone(
                    selectedBusiness.id,
                    bookingData.customer.id,
                    bookingId,
                    bookingNumber,
                    'email',
                    !!paymentLinkHtml,
                    bookingData.customer.email || undefined,
                    customerName || undefined
                  );
                  console.log('✅ Invoice sent milestone created');
                } catch (milestoneError) {
                  console.error('❌ Failed to create invoice sent milestone:', milestoneError);
                  // Don't block the invoice send if milestone creation fails
                }
              }
            }
          } catch (error) {
            console.error('❌ Error creating invoice history record:', error);
          }
        }
        
        // Reset sending state
        setIsSendingEmail(false);
        
        // Refresh invoice history (+ standalone payments so "Sent" status updates)
        await loadInvoiceHistory();
        if (isStandalone) await reloadStandalone();
        
        // Refresh the booking data to show updated invoice sent status
        if (onUpdate) {
          try {
            // Call onUpdate to refresh the booking data
            const result = onUpdate();
            // Handle both sync and async onUpdate functions
            if (result instanceof Promise) {
              await result;
            }
            console.log('✅ Booking data refreshed after invoice send');
          } catch (error) {
            console.error('❌ Failed to refresh booking data:', error);
          }
        }
      } else {
        console.error('❌ Failed to send invoice in background:', result.error);
        if (loadingToast) {
          portalToast.dismiss(loadingToast);
        }
        const errorMsg = result.error || 'Unknown error';
        const lowerErr = errorMsg.toLowerCase();
        if (lowerErr.includes('invalid') && (lowerErr.includes('email') || lowerErr.includes('recipient') || lowerErr.includes('address'))) {
          portalToast.error(`Invalid email address. Please check the customer's email and try again.`);
        } else if (lowerErr.includes('gmail') || lowerErr.includes('auth') || lowerErr.includes('token') || lowerErr.includes('credential')) {
          portalToast.error('Gmail is not connected or authorization expired. Please reconnect Gmail in Integrations.');
        } else if (lowerErr.includes('rate') || lowerErr.includes('limit') || lowerErr.includes('quota')) {
          portalToast.error('Email sending limit reached. Please wait a moment and try again.');
        } else {
          portalToast.error(`Failed to send invoice: ${errorMsg}`);
        }
        setIsSendingEmail(false);
      }
      
    } catch (error) {
      console.error('❌ Error in background invoice processing:', error);
      if (loadingToast) {
        portalToast.dismiss(loadingToast);
      }
      const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred';
      portalToast.error(`Failed to send invoice: ${errorMsg}`);
      setIsSendingEmail(false);
    }
  };

  // Called from InvoiceSMSModal with user-edited message
  const handleSendInvoiceSMS = async (
    customMessage: string,
    allowStripePay: boolean,
    depositAmount?: number,
    saveCardOnFile?: boolean,
    recipientPhone?: string,
  ) => {
    if (!selectedBusiness) {
      portalToast.error('No business selected');
      return;
    }

    // Recipient priority: one-off override → invoice bill-to → customer's saved phone.
    const customerPhone = (recipientPhone?.trim() || billTo.phone?.trim() || bookingData.customer?.phone?.trim() || '');
    if (!customerPhone) {
      portalToast.error('No phone number to send to. Enter a recipient number or add one to the customer.');
      return;
    }

    setIsInvoiceSMSModalOpen(false);

    try {
      setIsSendingEmail(true);

      const loadingToast = portalToast.loading(
        <div>
          <div>Sending invoice via SMS...</div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>don't close booking</div>
        </div>
      );

      const pdfData = await generateInvoicePDF();
      if (!pdfData) {
        if (loadingToast) portalToast.dismiss(loadingToast);
        portalToast.error('Failed to generate invoice PDF');
        setIsSendingEmail(false);
        return;
      }

      const currentTotal = effectiveTotal;
      const payments = booking?.payments || bookingData.payments || [];
      const validPayments = payments.filter((payment: any) =>
        payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
      );
      const totalPaidCalc = invoice
        ? effectivePaid
        : validPayments.reduce((sum: number, payment: any) => sum + paymentPrincipal(payment), 0);
      const isDeposit = depositAmount !== undefined && depositAmount !== null;
      const amountOwing = invoice ? balanceOwing : Math.max(0, currentTotal - totalPaidCalc);
      const paymentAmount = isDeposit ? depositAmount : amountOwing;

      const crmCustomerId =
        bookingData.customer?.id ?? (booking as { customer?: { id?: string } } | undefined)?.customer?.id;

      let paymentUrl: string | null = null;
      let unpaidPayment: any = null;
      const includePaymentLink =
        allowStripePay &&
        amountOwing > 0 &&
        !!selectedBusiness?.stripe_connect_account_id &&
        selectedBusiness?.online_payments_enabled !== false;

      if (includePaymentLink) {
        try {
          unpaidPayment = payments.find((p: any) => {
            if (isDeposit) {
              return p.status === 'pending' && p.payment_type === 'deposit' && Math.abs((p.amount || 0) - paymentAmount) < 0.01;
            } else {
              return p.status === 'pending' && (p.amount === 0 || p.reference === 'unpaid-invoice');
            }
          });

          if (!unpaidPayment) {
            const { data: newPayment, error: paymentError } = await supabase
              .from('payments')
              .insert({
                business_id: selectedBusiness?.id || '',
                ...(isStandalone ? { invoice_id: invoiceId, booking_id: null } : { booking_id: bookingId }),
                amount: isDeposit ? paymentAmount : 0,
                method: 'credit_card',
                status: 'pending',
                reference: isDeposit ? 'deposit-invoice' : 'unpaid-invoice',
                notes: isDeposit
                  ? `Deposit invoice #${displayNumber} - Deposit: $${paymentAmount.toFixed(2)}`
                  : `Unpaid invoice #${displayNumber}`,
                payment_token: crypto.randomUUID(),
                payment_type: isDeposit ? 'deposit' : 'final',
                save_card_on_file: !!(saveCardOnFile && crmCustomerId && allowStripePay),
                save_card_customer_id:
                  saveCardOnFile && crmCustomerId && allowStripePay ? crmCustomerId : null,
              })
              .select()
              .single();

            if (!paymentError && newPayment) {
              unpaidPayment = newPayment;
            }
          }

          if (unpaidPayment && allowStripePay) {
            const saveFlag = !!(saveCardOnFile && crmCustomerId);
            await supabase
              .from('payments')
              .update({
                save_card_on_file: saveFlag,
                save_card_customer_id: saveFlag ? crmCustomerId : null,
              })
              .eq('id', unpaidPayment.id);
          }

          if (unpaidPayment) {
            const { data: existingLink, error: linkCheckError } = await supabase
              .from('payment_links')
              .select('payment_url')
              .eq('payment_id', unpaidPayment.id)
              .eq('status', 'active')
              .single();

            if (!linkCheckError && existingLink?.payment_url) {
              paymentUrl = existingLink.payment_url;
            } else {
              const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
                body: { paymentId: unpaidPayment.id }
              });
              if (!linkError && linkData?.paymentUrl) {
                paymentUrl = linkData.paymentUrl;
              }
            }
          }
        } catch (paymentLinkError) {
          console.error('Error generating payment link for SMS:', paymentLinkError);
        }
      } else if (selectedBusiness?.id && isStandalone) {
        // Standalone PDF-only portal: reuse (or mint) a pending payment keyed on
        // the invoice — ensureInvoicePortalPayment requires a booking.
        try {
          let portalPayment: any = payments.find(
            (p: any) => p.status === 'pending' && p.payment_token,
          );
          if (!portalPayment) {
            const { data: newPayment, error: portalErr } = await supabase
              .from('payments')
              .insert({
                business_id: selectedBusiness.id,
                invoice_id: invoiceId,
                booking_id: null,
                amount: 0,
                method: 'credit_card',
                status: 'pending',
                reference: 'unpaid-invoice',
                notes: `Unpaid invoice #${displayNumber}`,
                payment_token: crypto.randomUUID(),
                payment_type: 'final',
              })
              .select()
              .single();
            if (portalErr) throw portalErr;
            portalPayment = newPayment;
          }
          unpaidPayment = { id: portalPayment.id };
          paymentUrl = buildPayInvoiceUrl(portalPayment.payment_token, { pdfOnly: true });
        } catch (e) {
          console.error('Failed to prepare standalone invoice portal link:', e);
          if (loadingToast) portalToast.dismiss(loadingToast);
          portalToast.error('Failed to prepare invoice link');
          setIsSendingEmail(false);
          return;
        }
      } else if (selectedBusiness?.id) {
        const portalPayment = await ensureInvoicePortalPayment({
          businessId: selectedBusiness.id,
          bookingId,
          bookingNumber,
          existingPayments: payments,
        });
        if (!portalPayment) {
          if (loadingToast) portalToast.dismiss(loadingToast);
          portalToast.error('Failed to prepare invoice link');
          setIsSendingEmail(false);
          return;
        }
        unpaidPayment = { id: portalPayment.id };
        paymentUrl = buildPayInvoiceUrl(portalPayment.payment_token, { pdfOnly: true });
      }

      // Build final SMS: pay portal (both tabs) or PDF-only portal (same URL, Pay tab hidden)
      let smsMessage = customMessage;
      if (paymentUrl) {
        if (includePaymentLink) {
          if (isDeposit) {
            smsMessage += ` A deposit of $${paymentAmount.toFixed(2)} is required. View invoice and pay: ${paymentUrl}`;
          } else {
            smsMessage += ` Amount owing: $${amountOwing.toFixed(2)}. View invoice and pay: ${paymentUrl}`;
          }
        } else {
          smsMessage += ` View your invoice: ${paymentUrl}`;
        }
      }

      // Only link the saved message to the customer when we actually sent to the
      // customer's OWN number. If a one-off recipient override was used, let the
      // message group under the number it was sent to (don't force the customer).
      const normPhone = (p?: string | null) => (p || '').replace(/\D/g, '').slice(-10);
      const sentToCustomerOwnNumber =
        !!bookingData.customer?.phone && normPhone(customerPhone) === normPhone(bookingData.customer?.phone);

      const result = await sendSMS({
        businessId: selectedBusiness.id,
        to: customerPhone,
        message: smsMessage,
        customerId: sentToCustomerOwnNumber ? bookingData.customer?.id : undefined,
      });

      if (result.success) {
        if (loadingToast) portalToast.dismiss(loadingToast);
        portalToast.success('Invoice sent via SMS! ✅');

        if (selectedBusiness?.id) {
          try {
            const fullName = bookingData.customer?.full_name ||
              `${bookingData.customer?.first_name || ''} ${bookingData.customer?.last_name || ''}`.trim();

            await supabase.from('invoice_history').insert({
              business_id: selectedBusiness.id,
              booking_id: isStandalone ? null : bookingId,
              booking_number: isStandalone ? null : bookingNumber,
              invoice_id: isStandalone ? invoiceId : null,
              sent_via: 'sms',
              has_payment_link: !!includePaymentLink && !!paymentUrl,
              payment_link_url: paymentUrl,
              payment_id: unpaidPayment?.id || null,
              customer_email: null,
              customer_name: fullName || null
            });

            if (bookingData.customer?.id && bookingId) {
              try {
                await createInvoiceSentMilestone(
                  selectedBusiness.id,
                  bookingData.customer.id,
                  bookingId,
                  bookingNumber,
                  'sms',
                  !!(includePaymentLink && paymentUrl),
                  undefined,
                  fullName || undefined
                );
              } catch (milestoneError) {
                console.error('Failed to create invoice sent milestone:', milestoneError);
              }
            }
          } catch (historyError) {
            console.error('Error creating invoice history record:', historyError);
          }
        }

        // Refresh invoice history (+ standalone payments so "Sent" status updates)
        try {
          await loadInvoiceHistory();
          if (isStandalone) await reloadStandalone();
        } catch (e) { /* non-critical */ }

        if (onUpdate) {
          try {
            const r = onUpdate();
            if (r instanceof Promise) await r;
          } catch (e) { /* non-critical */ }
        }
      } else {
        if (loadingToast) portalToast.dismiss(loadingToast);
        const errorMsg = result.error || 'Unknown error';
        const lowerErr = errorMsg.toLowerCase();
        if (lowerErr.includes('twilio') || lowerErr.includes('credentials') || lowerErr.includes('not configured')) {
          portalToast.error('Twilio is not connected. Please set up Twilio in Integrations to send SMS.');
        } else if (lowerErr.includes('invalid') || lowerErr.includes('phone') || lowerErr.includes('number')) {
          portalToast.error(`Invalid phone number. Please check the customer's phone number and try again.`);
        } else {
          portalToast.error(`Failed to send invoice via SMS: ${errorMsg}`);
        }
      }

      setIsSendingEmail(false);
    } catch (error) {
      console.error('Error sending invoice via SMS:', error);
      const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred';
      portalToast.error(`Failed to send invoice via SMS: ${errorMsg}`);
      setIsSendingEmail(false);
    }
  };


  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      loadBookingProducts(); // Load products when modal opens
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Detect mobile view
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <>
      {/* Backdrop with blur - iOS style */}
      <div 
        className={`fixed inset-0 transition-opacity duration-300 ${
          isInvisible ? 'opacity-0 pointer-events-none' : (isOpen ? 'opacity-100' : 'opacity-0')
        }`}
        style={isInvisible ? { 
          position: 'fixed', 
          top: '-9999px', 
          left: '-9999px', 
          zIndex: -1,
          opacity: 0,
          pointerEvents: 'none'
        } : {
          zIndex: stackZ,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
        onClick={isInvisible ? undefined : onClose}
        onTransitionEnd={handleTransitionEnd}
      />
      
      {/* Modal Content - Full Screen iOS Style on Mobile, Right Side on Desktop */}
      <div 
        className={`fixed bg-white transform transition-transform duration-300 ease-out ${
          isMobile 
            ? `inset-0 ${isInvisible ? 'translate-y-0' : (isOpen ? 'translate-y-0' : 'translate-y-full')}`
            : `top-0 right-0 h-full ${isInvisible ? 'translate-x-0' : (isOpen ? 'translate-x-0' : 'translate-x-full')}`
        }`}
        style={isInvisible ? { 
          position: 'fixed', 
          top: '-9999px', 
          left: '-9999px', 
          zIndex: -1,
          width: isMobile ? '100vw' : '672px',
          height: '100vh',
          opacity: 0,
          pointerEvents: 'none'
        } : {
          zIndex: stackZ + 1,
          width: isMobile ? '100vw' : '672px',
          boxShadow: isMobile ? '0 -4px 20px rgba(0, 0, 0, 0.15)' : '-4px 0 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full" style={{ height: '100vh' }}>
          {/* Header with safe area for notch */}
          <div 
            className="bg-white border-b border-gray-100 flex items-center justify-between shrink-0"
            style={{ paddingTop: isMobile ? 'max(1rem, env(safe-area-inset-top))' : '1rem', paddingBottom: '0.75rem', paddingLeft: '1.25rem', paddingRight: '1.25rem' }}
          >
            <div className="flex items-center gap-3">
              {isMobile && (
                <div className="p-2 rounded-xl bg-gray-100">
                  <DollarSign className="h-5 w-5 text-gray-600" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-[17px] font-semibold text-gray-900 leading-tight">Invoice</h2>
                  {/* Locked = issued (past draft): booking edits no longer flow into it. */}
                  {isInvoiceLocked(invoice) && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-gray-900 text-white">
                      <Lock className="h-2.5 w-2.5" />
                      Final
                    </span>
                  )}
                  {/* Soft lock (open / partially_paid) → amendable. Paid = hard lock. */}
                  {!isStandalone && bookingId && (invoice?.status === 'open' || invoice?.status === 'partially_paid') && (
                    <button
                      onClick={() => setShowAmendInvoice(true)}
                      className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 active:bg-blue-100 touch-manipulation"
                    >
                      Amend
                    </button>
                  )}
                </div>
                {(invoice?.invoice_number || booking?.booking_number || bookingNumber) && (
                  <p className="text-xs text-gray-500 mt-0.5">#{invoice?.invoice_number
                    ? `${invoiceSettings?.invoice_prefix || 'INV-'}${invoice.invoice_number}`
                    : (booking?.booking_number || bookingNumber)}</p>
                )}
                {(() => {
                  // Connection line: booking, order, rental(s), counter sale —
                  // or "standalone" when the invoice was created by hand.
                  if (bookingId) {
                    return (
                      <p className="text-[11px] mt-0.5 text-gray-400">
                        Booking #{booking?.booking_number || bookingNumber}
                      </p>
                    );
                  }
                  if (!saSource) return null; // still resolving the connection
                  const parts: string[] = [];
                  if (saSource.orderNumber) parts.push(`Order ${saSource.orderNumber}`);
                  if (saSource.rentalNumbers.length > 0) parts.push(`Rental ${saSource.rentalNumbers.join(', ')}`);
                  if (saSource.subscriptionNumbers.length > 0) parts.push(`Subscription ${saSource.subscriptionNumbers.join(', ')}`);
                  if (saSource.saleNumber) parts.push(`Counter sale #${saSource.saleNumber}`);
                  return parts.length > 0 ? (
                    <p className="text-[11px] mt-0.5 text-gray-400">{parts.join(' · ')}</p>
                  ) : (
                    <p className="text-[11px] mt-0.5 text-amber-600 font-medium">Standalone invoice</p>
                  );
                })()}
                {import.meta.env.DEV && bookingId && !invoice && (
                  <button
                    type="button"
                    onClick={handleSyncToInvoicing}
                    disabled={syncingInvoice}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                  >
                    {syncingInvoice ? 'Syncing…' : 'Sync to new invoicing'}
                  </button>
                )}
                {invoice && (
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className="text-[11px] text-gray-500">Terms</span>
                    <select
                      value={invoice.payment_terms || 'due_on_receipt'}
                      onChange={(e) => handleTermChange(e.target.value as InvoiceTermCode)}
                      className="text-[11px] bg-gray-100 border-0 rounded-md px-1.5 py-0.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {DEFAULT_TERM_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                      <option value="custom">Custom</option>
                    </select>
                    {invoice.payment_terms === 'custom' ? (
                      <input
                        type="date"
                        value={invoice.due_at ? invoice.due_at.slice(0, 10) : ''}
                        onChange={(e) => handleCustomDueChange(e.target.value)}
                        className="text-[11px] bg-gray-100 border-0 rounded-md px-1.5 py-0.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      invoice.due_at && (
                        <span className="text-[11px] text-gray-500">
                          Due {new Date(invoice.due_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors active:bg-gray-200 active:scale-95 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-white" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="space-y-0">
              
              {/* Balance summary — one dominant number + status pill + quiet metrics */}
              <div className="bg-white border-b border-gray-200 p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      {balanceOwing > 0 ? 'Amount due' : 'Paid in full'}
                    </div>
                    <div className={`text-3xl font-bold mt-0.5 tracking-tight ${balanceOwing > 0 ? 'text-gray-900' : 'text-green-600'}`}>
                      ${(balanceOwing > 0 ? balanceOwing : effectivePaid).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  {(() => {
                    const cfg = invoice?.status === 'void'
                      ? { label: 'Void', cls: 'bg-gray-100 text-gray-500' }
                      : paymentStatus === 'paid'
                        ? { label: 'Paid', cls: 'bg-green-50 text-green-700 border border-green-200' }
                        : paymentStatus === 'partial'
                          ? { label: 'Partially paid', cls: 'bg-blue-50 text-blue-700 border border-blue-200' }
                          : { label: 'Unpaid', cls: 'bg-amber-50 text-amber-700 border border-amber-200' };
                    return (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.cls}`}>
                        {cfg.label}
                      </span>
                    );
                  })()}
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 text-[12px] text-gray-500">
                  <span>Total <span className="font-semibold text-gray-800">${effectiveTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                  <span>Paid <span className="font-semibold text-gray-800">${effectivePaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                  {totalDeposit > 0 && (
                    <span>Deposit <span className="font-semibold text-gray-800">${totalDeposit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                  )}
                  {totalTips > 0 && (
                    <span>Tips <span className="font-semibold text-gray-800">${totalTips.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                  )}
                  {(() => {
                    const payments = [...(booking?.payments || bookingData.payments || []), ...saPayments];
                    const hasInvoiceSent = payments.some((p: any) => p.payment_token || p.stripe_checkout_session_id);
                    return (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${hasInvoiceSent ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                        <Send className="h-2.5 w-2.5" />
                        {hasInvoiceSent ? 'Sent' : 'Not sent'}
                      </span>
                    );
                  })()}
                </div>

                {/* Payment History Dropdown */}
                <div className="mt-6 pt-6 border-t border-gray-200/60">
                  <button
                    onClick={() => setIsPaymentHistoryOpen(!isPaymentHistoryOpen)}
                    className="w-full flex items-center justify-between p-3 bg-white hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors duration-200 touch-manipulation border border-gray-200"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">Payment History</span>
                      <span className="text-xs text-gray-500">
                        {(() => {
                          const payments = [...(booking?.payments || bookingData.payments || []), ...allocatedPayments];
                          
                          // Count all entries - grouped entries count as 1, but we show all invoice history
                          const invoiceHistoryWithPaymentLink = invoiceHistory.filter(inv => inv.has_payment_link && inv.payment_id);
                          const paymentsInGroups = new Set(invoiceHistoryWithPaymentLink.map(inv => inv.payment_id).filter(Boolean));
                          const standalonePayments = payments.filter((p: any) => !paymentsInGroups.has(p.id));
                          const standaloneInvoices = invoiceHistory.filter(inv => !inv.has_payment_link || !inv.payment_id);
                          
                          // Count: grouped entries (1 per unique payment_id) + standalone payments + standalone invoices + past invoices
                          // For grouped entries, we count the unique payment_ids (each payment_id = 1 grouped entry)
                          // Plus all past invoices (they're shown separately)
                          const uniquePaymentIds = new Set(invoiceHistoryWithPaymentLink.map(inv => inv.payment_id).filter(Boolean));
                          const groupedCount = uniquePaymentIds.size;
                          const pastInvoiceCount = invoiceHistoryWithPaymentLink.length - groupedCount; // Past invoices beyond the most recent
                          const totalCount = standalonePayments.length + standaloneInvoices.length + groupedCount + pastInvoiceCount;
                          return `(${totalCount} ${totalCount === 1 ? 'entry' : 'entries'})`;
                        })()}
                      </span>
                    </div>
                    <div className={`transform transition-transform duration-200 ${isPaymentHistoryOpen ? 'rotate-180' : ''}`}>
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {isPaymentHistoryOpen && (
                    <div className="mt-4 space-y-3">
                      {(() => {
                        const payments = [...(booking?.payments || bookingData.payments || []), ...allocatedPayments];
                        
                        // Convert invoice history to display format
                        const invoiceHistoryEntries = invoiceHistory.map((invoice) => ({
                          id: invoice.id,
                          type: 'invoice',
                          created_at: invoice.created_at,
                          sent_via: invoice.sent_via,
                          has_payment_link: invoice.has_payment_link,
                          payment_id: invoice.payment_id,
                          customer_email: invoice.customer_email,
                          customer_name: invoice.customer_name
                        }));
                        
                        // Group invoice history with related payments
                        const groupedEntries: any[] = [];
                        const processedPaymentIds = new Set<string>();
                        const processedInvoiceIds = new Set<string>();
                        
                        // Find the most recent invoice for each payment_id (to identify which ones to grey out)
                        const mostRecentInvoiceByPaymentId = new Map<string, typeof invoiceHistoryEntries[0]>();
                        invoiceHistoryEntries.forEach((invoice) => {
                          if (invoice.has_payment_link && invoice.payment_id) {
                            const existing = mostRecentInvoiceByPaymentId.get(invoice.payment_id);
                            if (!existing || new Date(invoice.created_at) > new Date(existing.created_at)) {
                              mostRecentInvoiceByPaymentId.set(invoice.payment_id, invoice);
                            }
                          }
                        });
                        
                        // First, process the most recent invoice for each payment_id and group with payment
                        mostRecentInvoiceByPaymentId.forEach((invoice) => {
                          // Find the related payment
                          const relatedPayment = payments.find((p: any) => p.id === invoice.payment_id);
                          if (relatedPayment) {
                            // Group the most recent invoice with the payment
                            groupedEntries.push({
                              type: 'grouped',
                              invoice: invoice,
                              payment: relatedPayment,
                              isPastInvoice: false,
                              created_at: invoice.created_at
                            });
                            processedPaymentIds.add(relatedPayment.id);
                            processedInvoiceIds.add(invoice.id);
                          }
                        });
                        
                        // Then, add past invoices (older ones with same payment_id) as standalone greyed out entries
                        invoiceHistoryEntries.forEach((invoice) => {
                          if (invoice.has_payment_link && invoice.payment_id && !processedInvoiceIds.has(invoice.id)) {
                            // This is a past invoice - show as standalone greyed out
                            groupedEntries.push({
                              ...invoice,
                              type: 'invoice',
                              isPastInvoice: true
                            });
                            processedInvoiceIds.add(invoice.id);
                          } else if (!processedInvoiceIds.has(invoice.id)) {
                            // Invoice without payment link - standalone
                            groupedEntries.push(invoice);
                            processedInvoiceIds.add(invoice.id);
                          }
                        });
                        
                        // Add remaining payments that weren't grouped
                        payments.forEach((payment: any) => {
                          if (!processedPaymentIds.has(payment.id)) {
                            groupedEntries.push({
                              ...payment,
                              type: 'payment'
                            });
                          }
                        });
                        
                        // Sort all entries by created_at (newest first)
                        const sortedEntries = [...groupedEntries].sort((a, b) => 
                          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                        );
                        
                        if (sortedEntries.length === 0) {
                          return (
                            <div className="p-4 rounded-lg border bg-white border-gray-200">
                              <div className="text-center text-gray-500">
                                <div className="text-sm font-medium">No payment entries yet</div>
                                <div className="text-xs mt-1 text-gray-400">Payment records will appear here when created</div>
                              </div>
                            </div>
                          );
                        }
                        
                        return sortedEntries.map((entry, index) => {
                          // Handle grouped entries (invoice + payment)
                          if (entry.type === 'grouped') {
                            const invoice = entry.invoice;
                            const payment = entry.payment;
                            
                            // Determine payment status styling based on actual payment status
                            let paymentStatusConfig = {
                            color: 'text-gray-600',
                            bgColor: 'bg-gray-50',
                            borderColor: 'border-gray-200',
                            label: payment.status || 'Unknown',
                            icon: '●'
                          };
                          
                          switch (payment.status) {
                            case 'completed':
                            case 'succeeded':
                            case 'paid':
                                paymentStatusConfig = {
                                color: 'text-green-600',
                                bgColor: 'bg-green-50',
                                borderColor: 'border-green-200',
                                label: 'Completed',
                                icon: '✓'
                              };
                              break;
                            case 'pending':
                                paymentStatusConfig = {
                                color: 'text-yellow-600',
                                bgColor: 'bg-yellow-50',
                                borderColor: 'border-yellow-200',
                                label: payment.stripe_checkout_session_id ? 'Pending Stripe Pay' : 'Pending',
                                icon: '⏳'
                              };
                              break;
                            case 'failed':
                                paymentStatusConfig = {
                                color: 'text-red-600',
                                bgColor: 'bg-red-50',
                                borderColor: 'border-red-200',
                                label: 'Failed',
                                icon: '✗'
                              };
                              break;
                            case 'voided':
                                paymentStatusConfig = {
                                color: 'text-gray-500',
                                bgColor: 'bg-gray-100',
                                borderColor: 'border-gray-300',
                                label: 'Voided',
                                icon: '⊘'
                              };
                              break;
                            }
                            
                            const isPastInvoice = entry.isPastInvoice || false;
                            
                            return (
                              <div key={`grouped-${invoice.id}-${payment.id}`} className={`p-4 rounded-lg border transition-all duration-200 ${isPastInvoice ? 'bg-white border-gray-200 opacity-60' : 'bg-white border-gray-200'}`}>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
                                  <div className="flex items-start space-x-3 flex-1">
                                    <div className={`w-2 h-2 rounded-full ${isPastInvoice ? 'bg-gray-300' : 'bg-gray-400'} flex-shrink-0 mt-1.5`}></div>
                                    <div className="min-w-0 flex-1">
                                      <div className={`font-medium text-sm ${isPastInvoice ? 'text-gray-400' : 'text-gray-900'}`}>
                                        Invoice Sent - With Payment Link
                                        {isPastInvoice && <span className="ml-2 text-xs text-gray-400 font-normal">(Past)</span>}
                                      </div>
                                      <div className={`text-xs mt-1 ${isPastInvoice ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {new Date(invoice.created_at).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                        <span className="ml-2">
                                          • Sent via {invoice.sent_via}
                                        </span>
                                        {invoice.customer_email && (
                                          <span className="ml-2">
                                            • {invoice.customer_email}
                                          </span>
                                        )}
                                      </div>
                                      {/* Payment info inline - only show for most recent invoice */}
                                      {!isPastInvoice && (
                                        <div className="mt-2.5 pt-2.5 border-t border-gray-100">
                                          <div className="flex items-center space-x-2">
                                            <div className={`w-1.5 h-1.5 rounded-full ${paymentStatusConfig.color === 'text-green-600' ? 'bg-green-500' : paymentStatusConfig.color === 'text-yellow-600' ? 'bg-yellow-500' : paymentStatusConfig.color === 'text-red-600' ? 'bg-red-500' : 'bg-gray-400'} flex-shrink-0`}></div>
                                            <div className="text-xs text-gray-600">
                                              <div className="flex items-center gap-2 flex-wrap">
                                              <span className={`font-medium ${paymentStatusConfig.color === 'text-green-600' ? 'text-gray-900' : paymentStatusConfig.color}`}>
                                                {formatPaymentReference(payment.reference)} - {paymentStatusConfig.label}
                                              </span>
                                                {payment.payment_type && (
                                                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                                    payment.payment_type === 'deposit' 
                                                      ? 'bg-blue-100 text-blue-700' 
                                                      : payment.payment_type === 'final'
                                                      ? 'bg-green-100 text-green-700'
                                                      : 'bg-gray-100 text-gray-700'
                                                  }`}>
                                                    {payment.payment_type === 'deposit' ? 'Deposit' : payment.payment_type === 'final' ? 'Final Payment' : 'Partial'}
                                                  </span>
                                                )}
                                              </div>
                                              {payment.method && (
                                                <span className="ml-0 mt-1 block text-gray-500">
                                                  • {payment.method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                                </span>
                                              )}
                                              {payment.surcharge_amount > 0 && (
                                                <span className="ml-0 mt-1 block text-amber-600">
                                                  • Card Processing Fee: ${(payment.surcharge_amount || 0).toFixed(2)}
                                                </span>
                                              )}
                                              {payment.notes && !payment.surcharge_amount && (
                                                <span className="ml-0 mt-1 block text-gray-400">
                                                  • {payment.notes}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-end space-x-3 sm:ml-4">
                                    {!isPastInvoice && (
                                      <>
                                        <div className={`font-semibold text-base ${paymentStatusConfig.color === 'text-green-600' ? 'text-gray-900' : paymentStatusConfig.color}`}>
                                          ${(payment.amount || 0).toFixed(2)}
                                          {payment.surcharge_amount > 0 && (
                                            <div className="text-xs font-normal text-amber-600">
                                              incl. ${(payment.surcharge_amount).toFixed(2)} fee
                                            </div>
                                          )}
                                        </div>
                                        {payment.status === 'completed' && payment.amount > 0 && (
                                          <button
                                            onClick={() => {
                                              setSelectedPaymentToVoid(payment);
                                              setVoidMessage('');
                                              setIsVoidModalOpen(true);
                                            }}
                                            className="px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 active:bg-red-100 transition-colors duration-200 touch-manipulation flex-shrink-0"
                                            style={{ WebkitTapHighlightColor: 'transparent' }}
                                          >
                                            Void
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // Handle standalone invoice history entries (without payment link, or past invoices)
                          if (entry.type === 'invoice') {
                            const isPastInvoice = entry.isPastInvoice || false;
                            const hasPaymentLink = entry.has_payment_link || false;
                            
                            return (
                              <div key={entry.id || index} className={`p-4 rounded-lg border transition-all duration-200 ${isPastInvoice ? 'bg-white border-gray-200 opacity-60' : 'bg-white border-gray-200'}`}>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-2 h-2 rounded-full ${isPastInvoice ? 'bg-gray-300' : 'bg-gray-400'} flex-shrink-0`}></div>
                                    <div className="min-w-0 flex-1">
                                      <div className={`font-medium text-sm ${isPastInvoice ? 'text-gray-400' : 'text-gray-900'}`}>
                                        Invoice Sent - {hasPaymentLink ? 'With Payment Link' : 'Without Payment Link'}
                                        {isPastInvoice && <span className="ml-2 text-xs text-gray-400 font-normal">(Past)</span>}
                                      </div>
                                      <div className={`text-xs mt-1 ${isPastInvoice ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {new Date(entry.created_at).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                        <span className="ml-2">
                                          • Sent via {entry.sent_via}
                                        </span>
                                      </div>
                                      {entry.customer_email && (
                                        <div className={`text-xs mt-1 ${isPastInvoice ? 'text-gray-400' : 'text-gray-400'}`}>
                                          {entry.customer_email}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between sm:justify-end space-x-3">
                                    <div className={`font-semibold text-base ${isPastInvoice ? 'text-gray-400' : 'text-gray-900'}`}>
                                      $0.00
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // Handle standalone payment entries
                          const payment = entry;

                          
                          // Determine styling based on payment status
                          let statusConfig = {
                                color: 'text-gray-600',
                                bgColor: 'bg-gray-50',
                            borderColor: 'border-gray-200',
                            label: payment.status || 'Unknown',
                            icon: '●'
                          };
                          
                          switch (payment.status) {
                            case 'completed':
                            case 'succeeded':
                            case 'paid':
                              statusConfig = {
                                color: 'text-gray-900',
                                bgColor: 'bg-white',
                                borderColor: 'border-gray-200',
                                label: 'Completed',
                                icon: '✓'
                              };
                              break;
                            case 'pending':
                              statusConfig = {
                                color: 'text-gray-900',
                                bgColor: 'bg-white',
                                borderColor: 'border-gray-200',
                                label: payment.stripe_checkout_session_id ? 'Pending Stripe Pay' : 'Pending',
                                icon: '⏳'
                              };
                              break;
                            case 'failed':
                              statusConfig = {
                                color: 'text-gray-900',
                                bgColor: 'bg-white',
                                borderColor: 'border-gray-200',
                                label: 'Failed',
                                icon: '✗'
                              };
                              break;
                            case 'voided':
                              statusConfig = {
                                color: 'text-gray-400',
                                bgColor: 'bg-white',
                                borderColor: 'border-gray-200',
                                label: 'Voided',
                                icon: '⊘'
                              };
                              break;
                            default:
                              statusConfig = {
                                color: 'text-gray-900',
                                bgColor: 'bg-white',
                                borderColor: 'border-gray-200',
                                label: payment.status || 'Unknown',
                                icon: '●'
                              };
                          }
                          
                          // Determine status dot color
                          let statusDotColor = 'bg-gray-400';
                          if (payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid') {
                            statusDotColor = 'bg-green-500';
                          } else if (payment.status === 'pending') {
                            statusDotColor = 'bg-yellow-500';
                          } else if (payment.status === 'failed') {
                            statusDotColor = 'bg-red-500';
                          } else if (payment.status === 'voided') {
                            statusDotColor = 'bg-gray-300';
                          }
                          
                          return (
                            <div key={payment.id || index} className={`p-4 rounded-lg border transition-all duration-200 ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-2 h-2 rounded-full ${statusDotColor} flex-shrink-0`}></div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                    <div className={`font-medium text-sm ${statusConfig.color}`}>
                                      {formatPaymentReference(payment.reference)} - {statusConfig.label}
                                      </div>
                                      {payment.payment_type && (
                                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                          payment.payment_type === 'deposit' 
                                            ? 'bg-blue-100 text-blue-700' 
                                            : payment.payment_type === 'final'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-700'
                                        }`}>
                                          {payment.payment_type === 'deposit' ? 'Deposit' : payment.payment_type === 'final' ? 'Final Payment' : 'Partial'}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {new Date(payment.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                      {payment.method && (
                                        <span className="ml-2 text-gray-500">
                                          • {payment.method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                        </span>
                                      )}
                                    </div>

                                    {payment.stripe_payment_intent_id && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Paid with Stripe
                                      </div>
                                    )}
                                    {payment.surcharge_amount > 0 && (
                                      <div className="text-xs text-amber-600 mt-1">
                                        Card Processing Fee: ${(payment.surcharge_amount || 0).toFixed(2)}
                                      </div>
                                    )}
                                    {payment.notes && !payment.surcharge_amount && (
                                      <div className="text-xs text-gray-400 mt-1">
                                        {payment.notes}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between sm:justify-end space-x-3">
                                  <div className={`font-semibold text-base ${statusConfig.color}`}>
                                    ${(payment.amount || 0).toFixed(2)}
                                    {payment.surcharge_amount > 0 && (
                                      <div className="text-xs font-normal text-amber-600">
                                        incl. ${(payment.surcharge_amount).toFixed(2)} fee
                                      </div>
                                    )}
                                  </div>
                                  {/* Void button - only show for completed payments that aren't already voided */}
                                  {payment.status === 'completed' && payment.amount > 0 && !payment.is_allocation && (
                                    <button
                                      onClick={() => {
                                        setSelectedPaymentToVoid(payment);
                                        setVoidMessage('');
                                        setIsVoidModalOpen(true);
                                      }}
                                      className="px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 active:bg-red-100 transition-colors duration-200 touch-manipulation flex-shrink-0"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      Void
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Preview Section */}
              <div className="bg-white border-b border-gray-200/60 px-2 sm:px-6 py-4 sm:py-6">
                <div className="flex flex-row items-center gap-2.5 flex-wrap">
                      {/* Record payment — the standard payment modal, booking or standalone */}
                      {(!isStandalone
                        ? balanceOwing > 0
                        : !!saInvoice && saInvoice.status !== 'void' && (saInvoice.amount_due_cents || 0) > 0) && (
                        <button
                          onClick={() => setIsRecordPaymentOpen(true)}
                          disabled={saPayBusy}
                          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium bg-green-600 text-white rounded-xl hover:bg-green-700 active:bg-green-800 focus:outline-none disabled:opacity-50 transition-colors touch-manipulation shadow-sm"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <DollarSign className="w-4 h-4 mr-1.5" />
                          Record payment
                        </button>
                      )}
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSendDropdown(!showSendDropdown);
                          }}
                          disabled={isLoadingSettings || isSendingEmail}
                          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation shadow-sm"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {isSendingEmail ? (
                            <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          {isSendingEmail ? 'Sending...' : 'Send'}
                        </button>

                        {showSendDropdown && (
                          <div
                            className="absolute right-0 top-full mt-2 w-48 rounded-xl shadow-lg bg-white border border-gray-200 z-20 overflow-hidden"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => {
                                setShowSendDropdown(false);
                                setIsInvoiceEmailModalOpen(true);
                              }}
                              className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed"
                              style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Mail className="h-4 w-4 mr-3 text-blue-500" />
                              Email
                            </button>
                            <button
                              onClick={() => {
                                setShowSendDropdown(false);
                                setIsInvoiceSMSModalOpen(true);
                              }}
                              className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed border-t border-gray-100"
                              style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                            >
                              <MessageSquare className="h-4 w-4 mr-3 text-green-500" />
                              SMS
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Overflow "⋯": PDF for everyone; pay link / void for standalone */}
                      <div className="relative ml-auto">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMoreMenu(!showMoreMenu);
                            }}
                            disabled={saPayBusy || isDownloadingPDF}
                            className="inline-flex items-center justify-center p-2.5 border border-gray-300 bg-white text-gray-500 rounded-xl hover:bg-gray-50 active:bg-gray-100 focus:outline-none disabled:opacity-50 transition-colors touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label="More actions"
                          >
                            {isDownloadingPDF ? (
                              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <MoreHorizontal className="w-4 h-4" />
                            )}
                          </button>
                          {showMoreMenu && (
                            <div
                              className="absolute right-0 top-full mt-2 w-48 rounded-xl shadow-lg bg-white border border-gray-200 z-20 overflow-hidden"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => {
                                  setShowMoreMenu(false);
                                  void downloadInvoicePDF();
                                }}
                                disabled={isLoadingSettings || isDownloadingPDF}
                                className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed"
                                style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                              >
                                <svg className="h-4 w-4 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download PDF
                              </button>
                              {isStandalone && saInvoice && saInvoice.status !== 'void' && (saInvoice.amount_due_cents || 0) > 0 && (
                                <button
                                  onClick={() => {
                                    setShowMoreMenu(false);
                                    void saCreatePayLink();
                                  }}
                                  className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation border-t border-gray-100"
                                  style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <Link2 className="h-4 w-4 mr-3 text-blue-500" />
                                  Payment link
                                </button>
                              )}
                              {isStandalone && saInvoice && saInvoice.status !== 'void' && (saInvoice.amount_paid_cents || 0) === 0 && (
                                <button
                                  onClick={() => {
                                    setShowMoreMenu(false);
                                    void saVoidInvoice();
                                  }}
                                  className="w-full px-4 py-3 text-sm text-left text-red-600 hover:bg-red-50 active:bg-red-100 flex items-center transition-all duration-150 touch-manipulation border-t border-gray-100"
                                  style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <Ban className="h-4 w-4 mr-3" />
                                  Void invoice
                                </button>
                              )}
                            </div>
                          )}
                      </div>
                </div>

                {/* Standalone: copyable customer pay link */}
                {isStandalone && saPayLinkUrl && (
                  <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
                    <div className="text-[11px] font-semibold text-blue-700 mb-1">Customer pay link</div>
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={saPayLinkUrl}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 text-[12px] bg-white border border-blue-200 rounded-lg px-2 py-1.5 text-gray-700"
                      />
                      <button
                        onClick={saCopyPayLink}
                        className="text-[12px] font-medium text-blue-700 px-2.5 py-1.5 rounded-lg hover:bg-blue-100"
                      >
                        {saPayLinkCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="px-0 sm:px-6 lg:px-8 py-2 sm:py-6 lg:py-8">
                  {isLoadingSettings ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <div className="w-12 h-12 border-4 border-gradient-to-r from-blue-500 to-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-6 bg-gradient-to-r from-blue-500 to-purple-600 opacity-20"></div>
                        <p className="text-gray-700 font-medium text-lg">Loading invoice settings...</p>
                      </div>
                    </div>
                  ) : (
                    <>
                    <div className="invoice-preview bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 relative mx-auto max-w-2xl" style={{ 
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      width: isMobile ? '100%' : 'auto',
                      maxWidth: isMobile ? '100%' : '672px',
                      transform: isMobile ? 'scale(0.85)' : 'scale(1)',
                      transformOrigin: 'top center',
                      marginBottom: isMobile ? '1rem' : '0'
                    }}>
                      {/* Invoice Settings Button - Top Right Corner */}
                      <button
                        onClick={() => setIsInvoiceSettingsModalOpen(true)}
                        className="invoice-settings-btn absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 rounded-lg hover:bg-white active:bg-gray-50 transition-colors touch-manipulation shadow-sm border border-gray-200/50"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Settings className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Invoice Settings</span>
                      </button>
                    
                                        {/* Invoice Header */}
                    <div className="p-4 sm:p-6 lg:p-8" style={{ backgroundColor: invoiceSettings?.header_background || '#F8FAFC' }}>
                      <div className="flex flex-col sm:flex-row justify-between items-start space-y-4 sm:space-y-0">
                        <div className="w-full sm:w-auto">
                          {invoiceSettings?.include_logo && selectedBusiness?.booking_settings?.logo_url && (
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg mb-3 sm:mb-4 flex items-center justify-center text-gray-400 text-xs overflow-hidden">
                              <img 
                                src={selectedBusiness.booking_settings.logo_url} 
                                alt="Company Logo" 
                                className="w-full h-full object-contain"
                              />
                            </div>
                          )}
                          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: invoiceSettings?.primary_color || '#3B82F6' }}>
                            INVOICE
                          </h1>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">#{`${invoiceSettings?.invoice_prefix || 'INV-'}${invoice?.invoice_number || booking?.booking_number || bookingNumber}`}</p>
                        </div>
                        <div className="text-left sm:text-right w-full sm:w-auto">
                          <h2 className="text-lg sm:text-xl font-semibold" style={{ color: invoiceSettings?.text_color || '#374151' }}>
                            {invoiceSettings?.company_name || 'Your Business Name'}
                          </h2>
                          <div className="text-xs sm:text-sm text-gray-600 mt-2 whitespace-pre-line">
                            {invoiceSettings?.company_address || '123 Business Street\nCity, State 12345'}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
                            <div>{invoiceSettings?.company_phone || '(555) 123-4567'}</div>
                            <div className="break-all">{invoiceSettings?.company_email || 'info@yourbusiness.com'}</div>
                            <div className="break-all">{invoiceSettings?.company_website || 'www.yourbusiness.com'}</div>
                            {(selectedBusiness as any)?.gst_number && <div>GST #: {(selectedBusiness as any).gst_number}</div>}
                            {(selectedBusiness as any)?.pst_number && <div>PST #: {(selectedBusiness as any).pst_number}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
      
                                                            {/* Invoice Details */}
                    <div className="p-4 sm:p-6 lg:p-8">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
                        {/* Bill To - Mobile Optimized (custom-editable per invoice) */}
                        <div className="bg-gray-50 sm:bg-transparent rounded-lg sm:rounded-none p-3 sm:p-0">
                          {invoice?.id && (invoice.status !== 'void' || !!invoice.po_number) && (
                            <div className="mb-3">
                              <label htmlFor="invoice-po-number" className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">PO</label>
                              {invoice.status === 'void' ? (
                                <div className="text-sm font-medium text-gray-900">{invoice.po_number}</div>
                              ) : (
                                <input
                                  id="invoice-po-number"
                                  value={poDraft}
                                  onChange={(e) => setPoDraft(e.target.value)}
                                  onBlur={() => { void savePoNumber(); }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      (e.currentTarget as HTMLInputElement).blur();
                                    }
                                  }}
                                  placeholder="Add PO number"
                                  disabled={poSaving}
                                  className="w-full max-w-[220px] text-[13px] border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 disabled:opacity-60"
                                />
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-sm sm:text-base lg:text-lg text-gray-800" style={{ color: invoiceSettings?.text_color || '#374151' }}>Bill To:</h3>
                            {invoice?.id && !billToFormOpen && (
                              <button
                                type="button"
                                onClick={openBillToForm}
                                className="text-[11px] font-medium text-blue-600 hover:text-blue-800 px-1.5 py-0.5 rounded hover:bg-blue-50 transition-colors"
                                title="Bill someone else without changing the customer"
                              >
                                Edit
                              </button>
                            )}
                          </div>
                          {billToFormOpen ? (
                            <div className="space-y-1.5 max-w-xs">
                              <input value={billToDraft.name} onChange={(e) => setBillToDraft((d) => ({ ...d, name: e.target.value }))}
                                placeholder="Name" className="w-full text-[13px] border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white" />
                              <input value={billToDraft.email} onChange={(e) => setBillToDraft((d) => ({ ...d, email: e.target.value }))}
                                placeholder="Email" type="email" className="w-full text-[13px] border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white" />
                              <input value={billToDraft.phone} onChange={(e) => setBillToDraft((d) => ({ ...d, phone: e.target.value }))}
                                placeholder="Phone" className="w-full text-[13px] border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white" />
                              <input value={billToDraft.address} onChange={(e) => setBillToDraft((d) => ({ ...d, address: e.target.value }))}
                                placeholder="Address" className="w-full text-[13px] border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white" />
                              <div className="flex items-center gap-2 pt-1 flex-wrap">
                                <button type="button" onClick={saveBillTo} disabled={billToSaving}
                                  className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-semibold disabled:opacity-60">
                                  {billToSaving ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" onClick={() => setBillToFormOpen(false)} disabled={billToSaving}
                                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-[12px] font-medium">Cancel</button>
                                {billToCustomer && (
                                  <button type="button" onClick={resetBillToCustomer} disabled={billToSaving}
                                    className="px-2 py-1.5 text-[12px] font-medium text-gray-500 hover:text-gray-700"
                                    title="Restore the customer's own details">
                                    Reset to customer
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-600 text-sm sm:text-base space-y-1">
                              <div className="font-semibold text-gray-900">{billTo.name || 'Customer Name'}</div>
                              {billTo.email && <div className="break-all text-xs sm:text-sm">{billTo.email}</div>}
                              {billTo.phone && <div className="text-xs sm:text-sm">{billTo.phone}</div>}
                              {billTo.address && <div className="text-xs sm:text-sm">{billTo.address}</div>}
                            </div>
                          )}
                        </div>
                        
                        {/* Invoice Info - Mobile Optimized */}
                        <div className="bg-gray-50 sm:bg-transparent rounded-lg sm:rounded-none p-3 sm:p-0 text-left sm:text-right">
                          <div className="space-y-2 text-xs sm:text-sm lg:text-base">
                            <div className="flex justify-between sm:justify-end sm:space-x-4">
                              <span className="font-medium text-gray-700">Invoice Date:</span>
                              <span className="font-semibold">
                                {isStandalone
                                  ? formatCalendarDate(saInvoice?.finalized_at || saInvoice?.created_at)
                                  : formatBookingInvoiceDate(booking)}
                              </span>
                            </div>
                            <div className="flex justify-between sm:justify-end sm:space-x-4">
                              <span className="font-medium text-gray-700">Due Date:</span>
                              <span className="font-semibold">
                                {isStandalone
                                  ? formatCalendarDate(saInvoice?.due_at || saInvoice?.finalized_at || saInvoice?.created_at)
                                  : formatBookingInvoiceDueDate(booking)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                          
                      {/* Services Table */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 sm:mb-8">
                        {/* Desktop Table Header */}
                        <div className="hidden sm:block bg-gray-50 p-4">
                          <div className="grid gap-2 font-semibold text-gray-700" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                            <div>Service</div>
                            <div className="text-center">Quantity</div>
                            <div className="text-right">Rate</div>
                            <div className="text-right">Amount</div>
                          </div>
                        </div>
                        {/* Mobile Header */}
                        <div className="sm:hidden bg-gradient-to-r from-gray-100 to-gray-50 p-4 border-b border-gray-200">
                          <div className="font-bold text-gray-800 text-base">Services & Pricing</div>
                        </div>
                                                <div className="divide-y sm:divide-y-0">
                          {/* Standalone invoice: render its own line items (no booking). */}
                          {isStandalone && saLines.length > 0 ? (
                            saLines.map((line) => {
                              const qty = line.quantity || 1;
                              const rate = (line.unit_amount_cents || 0) / 100;
                              const gross = (line.subtotal_cents ?? line.unit_amount_cents * qty) / 100;
                              const net = (line.net_cents ?? line.subtotal_cents ?? 0) / 100;
                              const discounted = (line.discount_cents || 0) > 0;
                              return (
                                <div key={line.id} className="service-row p-2 sm:p-4">
                                  {/* Desktop Grid Layout */}
                                  <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                                    <div>
                                      <div className="font-medium">{line.description || 'Item'}</div>
                                      {!line.taxable && <div className="text-xs text-gray-500 mt-0.5">Tax exempt</div>}
                                    </div>
                                    <div className="text-center">{qty}</div>
                                    <div className="text-right">${rate.toFixed(2)}</div>
                                    <div className="text-right font-medium">
                                      {discounted ? (
                                        <>
                                          <span>${net.toFixed(2)}</span>
                                          <span className="text-gray-400 line-through text-xs ml-1">${gross.toFixed(2)}</span>
                                        </>
                                      ) : (
                                        `$${net.toFixed(2)}`
                                      )}
                                    </div>
                                  </div>
                                  {/* Mobile Card Layout */}
                                  <div className="sm:hidden mb-3">
                                    <div className="bg-gray-50 rounded-lg p-3 space-y-2 shadow-sm">
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1 min-w-0">
                                          <div className="font-semibold text-base text-gray-900">{line.description || 'Item'}</div>
                                          {!line.taxable && <div className="text-xs text-gray-500 mt-0.5">Tax exempt</div>}
                                        </div>
                                        <div className="ml-3 text-right">
                                          <div className="font-bold text-lg text-gray-900">${net.toFixed(2)}</div>
                                          {discounted && <div className="text-xs text-gray-400 line-through">${gross.toFixed(2)}</div>}
                                        </div>
                                      </div>
                                      <div className="flex justify-end pt-1">
                                        <div className="text-right text-xs text-gray-500">Qty: {qty} × ${rate.toFixed(2)}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : ((booking?.booking_services && booking.booking_services.length > 0) || 
                            (booking?.services && booking.services.length > 0)) ? (
                            (booking?.booking_services || booking?.services || []).map((service: any, index: number) => (
                              <div key={service.id || index} className="service-row p-2 sm:p-4">
                                {/* Desktop Grid Layout */}
                                <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                                  <div>
                                    <div className="font-medium">{service.name}</div>
                                    {invoiceSettings?.show_item_descriptions && service.description && (
                                      <div className="text-sm text-gray-600 mt-1">
                                        <RichTextDescription 
                                          description={service.description} 
                                          className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                                        />
                                      </div>
                                    )}
                                    {invoiceSettings?.show_vehicle && (service.vehicle_make || service.vehicle_model) && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {service.vehicle_year} {service.vehicle_make} {service.vehicle_model}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-center">1</div>
                                  <div className="text-right">${(service.price || 0).toFixed(2)}</div>
                                  <div className="text-right font-medium">
                                    {(() => { const d = serviceCompareAtDisplay(service); return d.struck != null ? (<><span>${d.net.toFixed(2)}</span><span className="text-gray-400 line-through text-xs ml-1">${d.struck.toFixed(2)}</span></>) : `$${d.net.toFixed(2)}`; })()}
                                  </div>
                                </div>
                                
                                {/* Desktop Add-ons */}
                                {(service.add_ons || service.addons || []).map((addon: any) => {
                                  const addonName = addon.name || addon.addon?.name || 'Add-on';
                                  const addonDesc = addon.description || addon.addon?.description || '';
                                  const aNet = addonLineNet(addon);
                                  return (
                                    <div key={addon.id} className="hidden sm:grid gap-2 bg-gray-50 mt-1" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                                      <div className="pl-4">
                                        <div className="text-gray-600 text-sm">+ {addonName}</div>
                                        {invoiceSettings?.show_item_descriptions && addonDesc && (
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            <RichTextDescription 
                                              description={addonDesc} 
                                              className="prose-xs text-gray-500" 
                                            />
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-center text-gray-500 text-sm">-</div>
                                      <div className="text-right text-gray-600 text-sm">${aNet.grossDollars.toFixed(2)}</div>
                                      <div className="text-right text-gray-600 text-sm">
                                        {aNet.discounted ? (<><span>${aNet.netDollars.toFixed(2)}</span><span className="text-gray-400 line-through text-[10px] ml-1">${aNet.grossDollars.toFixed(2)}</span></>) : `$${aNet.grossDollars.toFixed(2)}`}
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Mobile Card Layout */}
                                <div className="sm:hidden mb-3">
                                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 shadow-sm">
                                    {/* Service Name and Price Row */}
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-base text-gray-900">{service.name}</div>
                                      </div>
                                      <div className="ml-3 text-right">
                                        {(() => { const d = serviceCompareAtDisplay(service); return d.struck != null ? (<><div className="font-bold text-lg text-gray-900">${d.net.toFixed(2)}</div><div className="text-xs text-gray-400 line-through">${d.struck.toFixed(2)}</div></>) : (<div className="font-bold text-lg text-gray-900">${d.net.toFixed(2)}</div>); })()}
                                      </div>
                                    </div>
                                    
                                    {/* Description */}
                                    {invoiceSettings?.show_item_descriptions && service.description && (
                                      <div className="text-sm text-gray-600 leading-relaxed">
                                        <RichTextDescription 
                                          description={service.description} 
                                          className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                                        />
                                      </div>
                                    )}
                                    
                                    {/* Vehicle Info and Quantity/Rate */}
                                    <div className="flex justify-between items-center pt-1">
                                      <div className="flex-1">
                                        {invoiceSettings?.show_vehicle && (service.vehicle_make || service.vehicle_model) && (
                                          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded inline-block">
                                            {service.vehicle_year} {service.vehicle_make} {service.vehicle_model}
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-right text-xs text-gray-500">
                                        Qty: 1 × ${(service.price || 0).toFixed(2)}
                                      </div>
                                    </div>

                                    {/* Mobile Add-ons */}
                                    {(service.add_ons || service.addons || []).map((addon: any) => {
                                      const addonName = addon.name || addon.addon?.name || 'Add-on';
                                      const aNet = addonLineNet(addon);
                                      return (
                                        <div key={addon.id} className="flex justify-between items-center pt-1 pl-2 border-t border-gray-200/60">
                                          <div className="text-sm text-gray-600">+ {addonName}</div>
                                          <div className="text-sm font-medium text-gray-700">
                                            {aNet.discounted ? (<><span>${aNet.netDollars.toFixed(2)}</span><span className="text-gray-400 line-through text-[10px] ml-1">${aNet.grossDollars.toFixed(2)}</span></>) : `$${aNet.grossDollars.toFixed(2)}`}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            ))
                                                    ) : (
                            <div className="service-row p-2 sm:p-4">
                              {/* Desktop Grid Layout */}
                              <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                                <div>
                                  <div className="font-medium">Booking Service</div>
                                  {invoiceSettings?.show_item_descriptions && (
                                    <div className="text-sm text-gray-600 mt-1">
                                      <RichTextDescription 
                                        description="Professional service" 
                                        className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="text-center">1</div>
                                <div className="text-right">${totalAmount.toFixed(2)}</div>
                                <div className="text-right font-medium">${(1 * totalAmount).toFixed(2)}</div>
                              </div>
                              
                              {/* Mobile Card Layout */}
                              <div className="sm:hidden mb-3">
                                <div className="bg-gray-50 rounded-lg p-3 space-y-2 shadow-sm">
                                  {/* Service Name and Price Row */}
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-semibold text-base text-gray-900">Booking Service</div>
                                    </div>
                                    <div className="ml-3 text-right">
                                      <div className="font-bold text-lg text-gray-900">${(1 * totalAmount).toFixed(2)}</div>
                                    </div>
                                  </div>
                                  
                                  {/* Description */}
                                  {invoiceSettings?.show_item_descriptions && (
                                    <div className="text-sm text-gray-600 leading-relaxed">
                                      <RichTextDescription 
                                        description="Professional service" 
                                        className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                                      />
                                    </div>
                                  )}
                                  
                                  {/* Quantity/Rate */}
                                  <div className="flex justify-end pt-1">
                                    <div className="text-right text-xs text-gray-500">
                                      Qty: 1 × ${totalAmount.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                    </div>
                      </div>

                      {/* Products Table */}
                      {bookingProducts && bookingProducts.length > 0 && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 sm:mb-8">
                          {/* Desktop Table Header */}
                          <div className="hidden sm:block bg-gray-50 p-4">
                            <div className="grid gap-2 font-semibold text-gray-700" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                              <div>Product</div>
                              <div className="text-center">Quantity</div>
                              <div className="text-right">Rate</div>
                              <div className="text-right">Amount</div>
                            </div>
                          </div>
                          {/* Mobile Header */}
                          <div className="sm:hidden bg-gradient-to-r from-gray-100 to-gray-50 p-4 border-b border-gray-200">
                            <div className="font-bold text-gray-800 text-base">Products & Pricing</div>
                          </div>
                          <div className="divide-y sm:divide-y-0">
                            {bookingProducts.map((product: any, index: number) => (
                              <div key={product.id || index} className="service-row p-2 sm:p-4">
                                {/* Desktop Grid Layout */}
                                <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                                  <div>
                                    <div className="font-medium">{product.product_name}</div>
                                  </div>
                                  <div className="text-center">{product.quantity}</div>
                                  <div className="text-right">${(product.unit_price || 0).toFixed(2)}</div>
                                  <div className="text-right font-medium">${(product.total_price || 0).toFixed(2)}</div>
                                </div>
                                
                                {/* Mobile Card Layout */}
                                <div className="sm:hidden mb-3">
                                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 shadow-sm">
                                    {/* Product Name and Price Row */}
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-base text-gray-900">{product.product_name}</div>
                                      </div>
                                      <div className="ml-3 text-right">
                                        <div className="font-bold text-lg text-gray-900">${(product.total_price || 0).toFixed(2)}</div>
                                      </div>
                                    </div>
                                    
                                    {/* Quantity/Rate */}
                                    <div className="flex justify-end pt-1">
                                      <div className="text-right text-xs text-gray-500">
                                        Qty: {product.quantity} × ${(product.unit_price || 0).toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                                                                  {/* Totals - Mobile Optimized */}
                      <div className="invoice-totals flex justify-end">
                        <div className="w-full sm:w-80 lg:w-64">
                          <div className="bg-gray-50 sm:bg-transparent rounded-lg p-3 sm:p-0 space-y-2 text-sm sm:text-base">
                            {invoiceView ? (
                              <>
                                <div className="flex justify-between py-1">
                                  <span className="text-gray-700">Subtotal:</span>
                                  <span className="font-semibold">${invoiceView.subtotal.toFixed(2)}</span>
                                </div>
                                {invoiceView.discount > 0 ? (
                                  <div className="flex justify-between text-red-600 py-1">
                                    <span>Discount:</span>
                                    <span className="font-semibold">-${invoiceView.discount.toFixed(2)}</span>
                                  </div>
                                ) : null}
                                {(invoiceSettings?.show_tax_breakdown && invoiceView.taxTotal > 0) ? (
                                  invoiceView.taxLines.length > 0 ? (
                                    invoiceView.taxLines.map((line, i) => (
                                      <div key={`inv-tax-${i}`} className="flex justify-between text-gray-600 py-1">
                                        <span>{line.label}:</span>
                                        <span className="font-semibold">${line.amount.toFixed(2)}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="flex justify-between text-gray-600 py-1">
                                      <span>Tax:</span>
                                      <span className="font-semibold">${invoiceView.taxTotal.toFixed(2)}</span>
                                    </div>
                                  )
                                ) : null}
                              </>
                            ) : (
                              <>
                            <div className="flex justify-between py-1">
                              <span className="text-gray-700">Subtotal:</span>
                              <span className="font-semibold">${(booking?.subtotal || totalAmount).toFixed(2)}</span>
                            </div>
                            {/* Show discounts if they exist */}
                            {(booking?.discount_amount != null && Number(booking.discount_amount) > 0) ? (
                              <div className="flex justify-between text-red-600 py-1">
                                <span>Discount:</span>
                                <span className="font-semibold">-${Number(booking.discount_amount).toFixed(2)}</span>
                              </div>
                            ) : null}
                            {(invoiceSettings?.show_tax_breakdown && (booking?.tax_amount || 0) > 0) ? (
                              (() => {
                                const additionalTotal = additionalTaxLines.reduce((s, l) => s + l.amount, 0);
                                const businessTaxTotal = (booking?.tax_amount || 0) - additionalTotal;
                                const combinedRate = businessTaxRates.reduce((s, r) => s + r.rate, 0);
                                const bookingSubtotal = Number(booking?.subtotal) || 0;
                                const bookingDiscount = Number(booking?.discount_amount) || 0;
                                const gstBase = Math.max(
                                  0,
                                  Math.round((bookingSubtotal - bookingDiscount) * 100) / 100,
                                );
                                const fmtBase = (n: number) =>
                                  '$' + (Math.round(n * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                return (
                                  <>
                                    {businessTaxRates.length > 0 ? (
                                      businessTaxRates.map((rate, i) => (
                                        <div key={`biz-${i}`} className="flex justify-between text-gray-600 py-1">
                                          <span>
                                            {rate.name} ({rate.rate}% on {fmtBase(gstBase)}):
                                          </span>
                                          <span className="font-semibold">${(combinedRate > 0 ? businessTaxTotal * rate.rate / combinedRate : 0).toFixed(2)}</span>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="flex justify-between text-gray-600 py-1">
                                        <span>Tax:</span>
                                        <span className="font-semibold">${businessTaxTotal.toFixed(2)}</span>
                                      </div>
                                    )}
                                    {additionalTaxLines.map((line, i) => (
                                      <div key={`add-${i}`} className="flex justify-between text-gray-600 py-1">
                                        <span>
                                          {line.name} ({line.rate}% on {fmtBase(line.taxableBase ?? 0)}):
                                        </span>
                                        <span className="font-semibold">${line.amount.toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </>
                                );
                              })()
                            ) : null}
                              </>
                            )}
                            <div className="border-t pt-3 mt-3">
                              <div className="flex justify-between font-bold text-lg sm:text-xl bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent sm:text-gray-900" style={{ color: invoiceSettings?.accent_color || '#059669' }}>
                                <span>Total:</span>
                                <span>${effectiveTotal.toFixed(2)}</span>
                              </div>
                              
                                                            {/* Payment breakdown with tip display - only show if payments exist */}
                              {(() => {
                                const currentTotal = effectiveTotal;
                                const payments = booking?.payments || bookingData.payments || [];
                                const validPayments = payments.filter((payment: any) => 
                                  payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
                                );
                                
                                // Calculate base payments (excluding tips) — prefer the invoice rollup.
                                const totalPaid = invoice
                                  ? effectivePaid
                                  : validPayments.reduce((sum: number, payment: any) => sum + paymentPrincipal(payment), 0);
                                
                                // Tips + surcharge prefer the invoice rollup.
                                const totalTips = invoice
                                  ? invoice.tips_cents / 100
                                  : validPayments.reduce((sum: number, payment: any) => sum + (payment.tip_amount || 0), 0);
                                
                                const totalSurchargesInvoice = invoice
                                  ? invoice.surcharge_cents / 100
                                  : validPayments.reduce(
                                      (sum: number, payment: any) => sum + (Number(payment.surcharge_amount) || 0),
                                      0,
                                    );
                                const totalReceived = invoice
                                  ? totalPaid + totalSurchargesInvoice + totalTips
                                  : validPayments.reduce((sum: number, payment: any) => sum + paymentGross(payment), 0) + totalTips;
                                
                                const amountOwing = invoice ? balanceOwing : Math.max(0, currentTotal - totalPaid);
                                
                                if (validPayments.length > 0) {
                                  return (
                                    <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200 space-y-1">
                                      <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Amount Paid:</span>
                                        <span className="text-green-600 font-medium">${totalPaid.toFixed(2)}</span>
                                      </div>
                                      {totalSurchargesInvoice > 0 && (
                                        <div className="flex justify-between text-xs sm:text-sm">
                                          <span className="text-amber-600">Card Processing Fees:</span>
                                          <span className="text-amber-600 font-medium">${totalSurchargesInvoice.toFixed(2)}</span>
                                        </div>
                                      )}
                                      {totalTips > 0 && (
                                        <div className="flex justify-between text-xs sm:text-sm">
                                          <span className="text-gray-600">Tips Received:</span>
                                          <span className="text-green-600 font-medium">${totalTips.toFixed(2)}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Total Received:</span>
                                        <span className="text-green-600 font-medium">${totalReceived.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Amount Owing:</span>
                                        <span className={`font-medium ${amountOwing > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                          ${amountOwing.toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                                            {/* Footer - Mobile Optimized */}
                      <div className="invoice-footer mt-6 sm:mt-8 lg:mt-12 pt-4 sm:pt-6 lg:pt-8 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-8">
                          <div className="bg-gray-50 sm:bg-transparent rounded-lg p-3 sm:p-0">
                            <h4 className="font-semibold mb-2 text-sm sm:text-base text-gray-800">Payment Terms:</h4>
                            <p className="text-xs sm:text-sm text-gray-600 font-medium">{invoiceSettings?.payment_terms || 'On receipt'}</p>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2 leading-relaxed">{invoiceSettings?.late_fees_policy || '1.5% monthly service charge on overdue amounts'}</p>
                          </div>
                          <div className="text-center sm:text-right bg-gray-50 sm:bg-transparent rounded-lg p-3 sm:p-0 flex items-center justify-center sm:block">
                            <p className="text-sm sm:text-base lg:text-lg font-semibold" style={{ color: invoiceSettings?.accent_color || '#059669' }}>
                              {invoiceSettings?.thank_you_message || 'Thank you for your business!'}
                            </p>
                          </div>
                        </div>
                      </div>
                                             </div>
                     </div>
                    </>
                   )}
                 </div>
                </div>
                               </div>
            </div>
          </div>
        </div>
    </>
  );

  // Handle invoice settings change
  const handleInvoiceSettingsChange = async (newSettings: InvoiceSettings) => {
    setInvoiceSettings(newSettings);
    // Reload settings to ensure we have the latest
    if (selectedBusiness?.id) {
      const updatedSettings = await InvoiceSettingsService.getInvoiceSettings(selectedBusiness.id);
      if (updatedSettings) {
        setInvoiceSettings(updatedSettings);
      }
    }
  };

  return (
    <ModalStackProvider zIndex={stackZ}>
      {createPortal(modalContent, document.body)}
      <InvoiceEmailModal
        isOpen={isInvoiceEmailModalOpen}
        onClose={() => setIsInvoiceEmailModalOpen(false)}
        onSend={handleSendInvoiceEmail}
        customerEmail={billTo.email || ''}
        customerName={billTo.name || bookingData.customer?.full_name || bookingData.customer?.first_name || 'Customer'}
        businessName={selectedBusiness?.name || 'Business'}
        bookingNumber={bookingNumber}
        hasStripeConnected={!!selectedBusiness?.stripe_connect_account_id && selectedBusiness?.online_payments_enabled !== false}
        amountOwing={balanceOwing}
        canOfferSaveCardOnFile={
          !!(bookingData.customer?.id ?? (booking as { customer?: { id?: string } } | undefined)?.customer?.id)
        }
        allowEditRecipient
        isSending={isSendingEmail}
      />
      <InvoiceSMSModal
        isOpen={isInvoiceSMSModalOpen}
        onClose={() => setIsInvoiceSMSModalOpen(false)}
        onSend={handleSendInvoiceSMS}
        customerPhone={billTo.phone || ''}
        customerName={(billTo.name || bookingData.customer?.first_name || 'there').split(/\s+/)[0]}
        businessName={selectedBusiness?.name || 'Business'}
        bookingNumber={bookingNumber}
        hasStripeConnected={!!selectedBusiness?.stripe_connect_account_id && selectedBusiness?.online_payments_enabled !== false}
        amountOwing={balanceOwing}
        canOfferSaveCardOnFile={
          !!(bookingData.customer?.id ?? (booking as { customer?: { id?: string } } | undefined)?.customer?.id)
        }
        allowEditRecipient
        isSending={isSendingEmail}
      />
      <InvoiceSettingsModal
        isOpen={isInvoiceSettingsModalOpen}
        onClose={() => setIsInvoiceSettingsModalOpen(false)}
        onSettingsChange={handleInvoiceSettingsChange}
        currentSettings={invoiceSettings}
      />

      {/* Apply Payment → the standard payment modal for booking invoices */}
      {!isStandalone && selectedBusiness?.id ? (
        <RecordPaymentModal
          isOpen={isRecordPaymentOpen}
          onClose={() => setIsRecordPaymentOpen(false)}
          onSuccess={() => {
            setIsRecordPaymentOpen(false);
            if (onUpdate) {
              try {
                const r = onUpdate();
                if (r instanceof Promise) r.catch(() => { /* non-critical */ });
              } catch { /* non-critical */ }
            }
          }}
          bookingId={bookingId}
          businessId={selectedBusiness.id}
          bookingTotal={effectiveTotal}
          amountPaid={effectivePaid}
          customerId={bookingData.customer?.id}
        />
      ) : null}

      {/* Record payment → the same standard payment modal for standalone
          invoices (orders, rentals, counter sales, manual invoices) */}
      {isStandalone && saInvoice ? (
        <RecordPaymentModal
          isOpen={isRecordPaymentOpen}
          onClose={() => setIsRecordPaymentOpen(false)}
          onSuccess={() => {
            setIsRecordPaymentOpen(false);
            void reloadStandalone();
            if (onUpdate) {
              try {
                const r = onUpdate();
                if (r instanceof Promise) r.catch(() => { /* non-critical */ });
              } catch { /* non-critical */ }
            }
          }}
          invoiceId={saInvoice.id}
          businessId={saInvoice.business_id}
          customerId={saInvoice.customer_id || undefined}
          bookingTotal={(saInvoice.total_cents || 0) / 100}
          amountPaid={(saInvoice.amount_paid_cents || 0) / 100}
        />
      ) : null}

      {/* Amend a soft-locked booking invoice */}
      {!isStandalone && bookingId && selectedBusiness?.id && showAmendInvoice && (
        <AmendInvoiceModal
          isOpen={showAmendInvoice}
          onClose={() => setShowAmendInvoice(false)}
          bookingId={bookingId}
          businessId={selectedBusiness.id}
          onAmended={() => {
            void refetchInvoice();
            if (onUpdate) {
              try {
                const r = onUpdate();
                if (r instanceof Promise) r.catch(() => { /* non-critical */ });
              } catch { /* non-critical */ }
            }
          }}
        />
      )}

      {/* Portal Toast for Modal Context */}
      {showPortalToast && (
        <PortalToast
          type={portalToastType}
          onClose={() => setShowPortalToast(false)}
        >
          {portalToastMessage}
        </PortalToast>
      )}

      {/* Void Payment Modal - iOS Style */}
      {isVoidModalOpen && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{
              zIndex: stackZ + 10,
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              animation: 'fadeIn 0.2s ease-out forwards',
            }}
            onClick={() => {
              setIsVoidModalOpen(false);
              setSelectedPaymentToVoid(null);
              setVoidMessage('');
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Modal Content */}
            <div 
              className="bg-white rounded-[20px] p-6 max-w-md w-full shadow-xl"
              style={{
                zIndex: stackZ + 11,
                animation: 'fadeIn 0.2s ease-out 0.05s forwards',
                opacity: 0,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-[20px] font-semibold text-gray-900 mb-6">Void Payment</h3>
          
              {selectedPaymentToVoid && (
                <div className="mb-6 p-4 bg-gray-50/80 rounded-xl border border-gray-200/50">
                  <div className="text-sm text-gray-600 font-medium mb-1">Payment Amount:</div>
                  <div className="text-2xl font-bold text-green-600">
                    ${selectedPaymentToVoid.amount?.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-2 font-mono break-all">
                    Payment ID: {selectedPaymentToVoid.id}
                  </div>
                </div>
              )}
                
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Void Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={voidMessage}
                  onChange={(e) => setVoidMessage(e.target.value)}
                  placeholder="Enter reason for voiding this payment..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm resize-none"
                  rows={3}
                />
              </div>
                
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsVoidModalOpen(false);
                    setSelectedPaymentToVoid(null);
                    setVoidMessage('');
                  }}
                  disabled={isVoidingPayment}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl focus:outline-none transition-all duration-200 touch-manipulation ${
                    isVoidingPayment
                      ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Cancel
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    confirmVoidPayment();
                  }}
                  disabled={!voidMessage.trim() || isVoidingPayment}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl focus:outline-none transition-all duration-200 touch-manipulation ${
                    voidMessage.trim() && !isVoidingPayment
                      ? 'text-white bg-red-600 hover:bg-red-700 active:bg-red-800'
                      : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {isVoidingPayment ? 'Voiding...' : 'Void Payment'}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </ModalStackProvider>
  );
} 